import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDropzone } from "react-dropzone";
import {
  Upload, Shield, Cloud, CheckCircle2, AlertCircle, AlertTriangle, FileText,
  RefreshCw, Archive, RotateCcw, FolderOpen, HardDrive, Zap, Lock, ChevronDown, MoreHorizontal, BarChart3, Download
} from "lucide-react";
import { computeFileHash, getPresignedUrl, uploadToS3, classifyFile, ingestFile, getFreshToken } from "../api";
import { useUploadQueue } from "../UploadQueueContext";
import { useProjects } from "../ProjectContext";
import { shouldShowBatch } from "../uploadProjectScope";
import { useFeature } from "../featureFlags";
import ProjectBar from "../components/ProjectBar";
import TableStructureView from "../components/TableStructureView";
import { filesSignature } from "../utils/tableStructure";
import { ToastView, ConfirmDialog, PromptDialog, useToast, useConfirm, usePrompt } from "../components/feedback";
import { friendlyFileName } from "../utils/fileName";
import { platformPillText, isDetected, platformOptions as readPlatformOptions, categoryPlatforms as readCategoryPlatforms } from "../utils/platformTag";
import * as XLSX from "xlsx";
import "./S3UploadPage.css";

const STATUS = { IDLE: "idle", HASHING: "hashing", REQUESTING: "requesting", UPLOADING: "uploading", DONE: "done", ERROR: "error", DUPLICATE: "duplicate", CHOOSE_ACTION: "choose_action" };
const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");

// friendlyFileName (strip the app-added UUID/id prefix for DISPLAY) now lives in
// ../utils/fileName so File Upload and Manage Projects show the exact same name.

// Integrity marker (roadmap #72): an amber ⚠ next to a file whose cleaning integrity
// check flagged something worth a look (e.g. a real number silently turned to 0, or
// unexpected data loss). Hover shows the one-line summary. Nothing renders on PASS/"".
function IntegrityMarker({ verdict, summary }) {
  const v = (verdict || "").toUpperCase();
  if (v !== "WARN" && v !== "BLOCK") return null;
  const color = v === "BLOCK" ? "#dc2626" : "#d97706";
  const label = v === "BLOCK" ? "BLOCKED" : "REVIEW";
  const tip = summary
    ? summary
    : (v === "BLOCK"
        ? "Integrity check blocked this file — open the cleaning report for details."
        : "Integrity check flagged this file — open the cleaning report for details.");
  // Filled pill, matching the verdict badge in the cleaning report, so a flagged file
  // is obvious at a glance (the bare icon was too subtle).
  return (
    <span title={tip} aria-label={`Data integrity ${v.toLowerCase()}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: "3px", marginLeft: "8px",
        padding: "1px 8px", borderRadius: "999px", background: color, color: "#fff",
        fontSize: "10px", fontWeight: 700, letterSpacing: "0.02em", flexShrink: 0,
        cursor: "help", verticalAlign: "middle", lineHeight: 1.6,
      }}>
      <AlertTriangle size={11} strokeWidth={2.5} /> {label}
    </span>
  );
}

// Inline QC chip for a dense file row (v2 redesign): a compact health badge derived
// from the SAME integrity verdict data IntegrityMarker uses. Green "✓ Clean" when the
// verdict is fine/absent, amber "⚠ Review" on WARN, red "✕ Blocked" on BLOCK. The
// tooltip carries the one-line summary (same source as IntegrityMarker).
function QcChip({ verdict, summary }) {
  const v = (verdict || "").toUpperCase();
  const flagged = v === "WARN" || v === "BLOCK";
  const blocked = v === "BLOCK";
  const color = blocked ? "#dc2626" : flagged ? "#d97706" : "#16a34a";
  const bg = blocked ? "#fee2e2" : flagged ? "#fef3c7" : "#dcfce7";
  const label = blocked ? "✕ Blocked" : flagged ? "⚠ Review" : "✓ Clean";
  const tip = summary
    ? summary
    : (flagged
        ? "Integrity check flagged this file — open the cleaning report for details."
        : "Cleaning integrity check passed.");
  return (
    <span className="s3upload-qc-chip" title={tip} aria-label={`Data quality ${label}`}
      style={{ color, background: bg }}>{label}</span>
  );
}

// Auto-retry fetch: refreshes token on 401 AND retries transient network
// failures (browser "Failed to fetch", e.g. Lambda cold-start blips) with backoff.
async function authFetch(url, options = {}, idToken, maxNetworkRetries = 2) {
  let token = idToken;
  const doFetch = (t) =>
    fetch(url, { ...options, headers: { ...options.headers, Authorization: `Bearer ${t}` } });

  let lastErr;
  for (let attempt = 0; attempt <= maxNetworkRetries; attempt++) {
    try {
      let res = await doFetch(token);
      if (res.status === 401) {
        // Token expired — get a fresh one and retry
        token = await getFreshToken();
        res = await doFetch(token);
      }
      return res;
    } catch (err) {
      // fetch() rejects (TypeError) only on network-level failures, not HTTP errors.
      lastErr = err;
      if (attempt < maxNetworkRetries) {
        await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
        continue;
      }
    }
  }
  throw lastErr;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function S3UploadPage({ idToken }) {
  const { activeProjectId, projects } = useProjects(); // reload the list on project switch; projects power "Move to project"
  const serverSideIngest = useFeature("server_side_ingest"); // Option B rollout valve: upload raw → server cleans/dedups
  const tableViewOn = useFeature("upload_table_structure_view"); // "Tables" tab (table → fields tree)
  const [tablesNonce, setTablesNonce] = useState(0); // bumped by the refresh icon so the Tables view refetches
  // In-app feedback (replaces native alert/confirm/prompt) — see components/feedback.jsx.
  const { toast, notify, dismiss } = useToast();
  const { confirm, confirmDialogProps } = useConfirm();
  const { prompt, promptDialogProps } = usePrompt();
  const [movingFile, setMovingFile] = useState(null); // file.key whose "Move to project" picker is open
  const [moveBusy, setMoveBusy] = useState(null);     // {name} while a move is in flight (shows a working toast)
  const [status, setStatus] = useState(STATUS.IDLE);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [fileName, setFileName] = useState(null);
  const [activeFiles, setActiveFiles] = useState([]);
  const [archivedFiles, setArchivedFiles] = useState([]);
  // fix-source-routing: the platform each data set comes from ({category: tag}) + the options.
  const [categoryPlatformMap, setCategoryPlatformMap] = useState({});
  const [platformChoices, setPlatformChoices] = useState([]);
  const [platformSaving, setPlatformSaving] = useState(null);   // category being saved
  const [platformError, setPlatformError] = useState(null);     // {category, text}
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [archivedFileKey, setArchivedFileKey] = useState(null);
  const [expandedFile, setExpandedFile] = useState(null); // key of file showing stats
  const [filesTab, setFilesTab] = useState("active"); // Active/Archived tabs (replaces the old showArchived toggle)
  // The Tables tab only exists while its flag is on; never leave the user on a hidden tab.
  useEffect(() => { if (!tableViewOn && filesTab === "tables") setFilesTab("active"); }, [tableViewOn, filesTab]);
  const [openMenuKey, setOpenMenuKey] = useState(null); // file.key whose ⋯ kebab menu is open
  const [reportDetailsOpen, setReportDetailsOpen] = useState(false); // "Details ▾" toggle inside the expanded cleaning report
  const [fileReport, setFileReport] = useState(null); // cleaning report data
  const [reportLoading, setReportLoading] = useState(false); // report fetch in progress
  const [reportNotReady, setReportNotReady] = useState(false); // report fetched null but file exists → "generating/retrying", not "none"
  // Mirror of expandedFile for use inside async report fetches: the retry below awaits a
  // delay, and a stale closure can't see that the user collapsed/switched files meanwhile.
  const expandedFileRef = useRef(null);
  useEffect(() => { expandedFileRef.current = expandedFile; }, [expandedFile]);
  const [searchFilter, setSearchFilter] = useState(""); // file list search
  const [categoryFilter, setCategoryFilter] = useState("all"); // category dropdown filter
  const [pendingUpload, setPendingUpload] = useState(null); // stores upload data when waiting for user choice (add/replace)
  // Multi-file SEQUENTIAL upload queue. null = no batch. Otherwise an array of
  // {name, status: 'queued'|'active'|'done'|'dup'|'failed', detail} + a `done` flag once finished.
  // Lives in an App-level provider (NOT local state) so it SURVIVES leaving this page and coming
  // back: the sequential upload loop keeps running after unmount and writes to the provider, so
  // returning to File Upload shows every file with its live status instead of a stale list.
  const { batchQueue, setBatchQueue, batchDone, setBatchDone, batchProjectId, setBatchProjectId } = useUploadQueue();

  // `silent` = refresh without the loading flicker (used by the visibility/interval auto-refresh
  // so a background poll doesn't blink the list). Mount/actions call it normally (with loading).
  const fetchFiles = useCallback(async (silent = false) => {
    if (!idToken) return;
    try {
      if (!silent) setLoadingFiles(true);
      const token = await getFreshToken() || idToken;
      // Pin to the active project explicitly. The global fetch wrapper injects
      // X-Project-Id from a module var that lags a switch by one effect cycle, so
      // relying on it would briefly load the previous project's files. Explicit wins.
      const res = await fetch(`${API_URL}/upload/files`, { headers: { Authorization: `Bearer ${token}`, "X-Project-Id": activeProjectId } });
      if (res.ok) {
        const data = await res.json();
        setActiveFiles(data.files || []); setArchivedFiles(data.archived_files || []);
        setCategoryPlatformMap(readCategoryPlatforms(data)); setPlatformChoices(readPlatformOptions(data));
      }
    } catch (err) { console.error("Failed to fetch files", err); }
    finally { if (!silent) setLoadingFiles(false); }
  }, [idToken, activeProjectId]);

  // Reload files on mount AND whenever the active project changes (the list is
  // project-scoped, so switching must reflect the new project's files immediately).
  // Also reset the upload panel: a prior result (duplicate/complete/error) belongs to
  // the project it happened under and must not linger after switching projects. This
  // effect only re-runs on project/token change — not after an upload (those don't
  // change its deps) — so it won't clobber a fresh post-upload result.
  useEffect(() => {
    if (!idToken) return;
    setExpandedFile(null); setFileReport(null);
    setStatus(STATUS.IDLE); setMessage(""); setFileName(null);
    setProgress(0); setArchivedFileKey(null); setPendingUpload(null);
    fetchFiles();
  }, [fetchFiles, idToken, activeProjectId]);

  // Keep the list in step without a manual Refresh (tuning): re-fetch (silently) when the window
  // regains visibility/focus — so switching away mid-batch and coming back shows the files that
  // finished processing while you were gone, instead of a stale snapshot.
  useEffect(() => {
    if (!idToken) return undefined;
    const onVisible = () => { if (document.visibilityState === "visible") fetchFiles(true); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [idToken, fetchFiles]);

  // While a multi-file batch is still importing (files process one at a time server-side), poll
  // the list every few seconds so each file appears/updates as it completes — no manual Refresh.
  useEffect(() => {
    if (!(batchQueue && !batchDone)) return undefined;
    const id = setInterval(() => { if (document.visibilityState === "visible") fetchFiles(true); }, 4000);
    return () => clearInterval(id);
  }, [batchQueue, batchDone, fetchFiles]);

  // Fetch the cleaning report for a file key. Returns {report, status} where status is
  // the backend's file status ("" when no FileLog record matched). Separated out so the
  // one-shot retry below can reuse the exact same fetch.
  const fetchReportOnce = async (fileKey) => {
    const headers = { Authorization: `Bearer ${idToken}` };
    const res = await fetch(`${API_URL}/upload/report/${encodeURIComponent(fileKey)}`, { headers });
    if (!res.ok) return { report: null, status: null, ok: false };
    const data = await res.json();
    return { report: data.report ?? null, status: data.status ?? null, ok: true };
  };

  // Toggle file stats panel
  const toggleStats = async (fileKey) => {
    if (expandedFile === fileKey) {
      setExpandedFile(null);
      setFileReport(null);
      setReportNotReady(false);
      return;
    }
    setExpandedFile(fileKey);
    setFileReport(null);
    setReportNotReady(false);
    setReportDetailsOpen(false); // verdict leads; phase stats stay collapsed until the user opens Details
    setReportLoading(true);
    try {
      let { report, status, ok } = await fetchReportOnce(fileKey);
      // A null report right after a completed upload is usually a read-timing miss (the
      // finalized FileLog state hadn't propagated to this read yet), NOT a missing report.
      // Distinguish "not ready / generating" from "genuinely none": if the file record
      // exists (status !== "") or the fetch failed transiently, do ONE short auto-retry
      // before deciding. Only treat it as "no report exists" when the file still returns
      // null after the retry — then the terminal Reprocess copy is correct.
      if ((ok && report === null && status !== "") || !ok) {
        setReportNotReady(true);
        await new Promise((r) => setTimeout(r, 1500));
        // Bail out if the user collapsed/switched files while we waited.
        if (expandedFileRef.current !== fileKey) { setReportNotReady(false); return; }
        ({ report, status, ok } = await fetchReportOnce(fileKey));
        setReportNotReady(false);
      }
      if (expandedFileRef.current !== fileKey) return;
      setFileReport(report);
    } catch (err) { console.warn("Failed to fetch report", err); setReportNotReady(false); }
    finally { setReportLoading(false); }
  };

  const archiveFile = async (fileKey, category) => {
    // fixed-visual-blueprint Task 28 (R14.1/R14.6): non-blocking pre-flight — if a Blueprint is
    // published and this file's category feeds one or more of its charts, warn with the AFFECTED
    // COUNT before the archive confirm, so the customer can back out rather than discover
    // needs-data charts after the fact. Best-effort: any failure here just means no extra line in
    // the message, never blocks archiving (advisory only, never a hard gate).
    let affectedNote = "";
    if (category) {
      try {
        const token = await getFreshToken();
        const res = await fetch(`${API_URL}/blueprint/affected-visuals?category=${encodeURIComponent(category)}`,
          { headers: { Authorization: `Bearer ${token}`, "X-Project-Id": activeProjectId } });
        if (res.ok) {
          const d = await res.json();
          if (d.enabled && d.count > 0) {
            affectedNote = ` This file feeds ${d.count} chart${d.count > 1 ? "s" : ""} in your published template (${d.titles.join(", ")}) — ${d.count > 1 ? "they" : "it"} will show "Needs data" until this data is back.`;
          }
        }
      } catch (e) { /* advisory only — never block on this check failing */ }
    }
    // Show strong warning about data gap
    const confirmed = await confirm({
      title: "Archive this file?",
      message: "This may create a gap in your core data. Because Boomerang prevents duplication, once archived its unique records leave your active dataset. Re-uploading the same data later adds only NEW records — the archived ones won't return automatically. To recover them you'll need to Restore this file (it's re-processed and de-duplicated)." + affectedNote,
      confirmLabel: "Archive file",
      cancelLabel: "Cancel",
      tone: "warning",
    });
    if (!confirmed) return;

    setActionLoading(fileKey);
    try {
      const res = await fetch(`${API_URL}/upload/files/archive`, { method: "POST", headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" }, body: JSON.stringify({ file_key: fileKey }) });
      if (res.ok) {
        await fetchFiles();
        // Trigger anomaly detection — archiving changes active data
        fetch(`${API_URL}/ai/detect-anomalies`, { method: "POST", headers: { Authorization: `Bearer ${idToken}` } }).catch(() => {});
        notify("success", "File archived", "It's been removed from your active dataset. Restore it anytime to bring its unique rows back.");
      } else { const err = await res.json().catch(() => ({})); notify("error", "Couldn't archive", err.detail || "Please try again."); }
    } catch { notify("error", "Couldn't archive", "Something went wrong archiving this file. Please try again."); }
    finally { setActionLoading(null); }
  };

  // Reprocess: re-clean a file from its raw original and (optionally) assign a
  // proper category — promotes a PARTIAL ("general_data") file to PASS.
  const reprocessFile = async (fileKey) => {
    const target = await prompt({
      title: "Reprocess this file",
      message: "Re-clean it from the original and optionally assign a category. Enter a category name (lowercase_with_underscores), or leave blank to let AI re-classify.",
      placeholder: "e.g. sales_orders (optional)",
      confirmLabel: "Reprocess",
    });
    if (target === null) return; // cancelled
    setActionLoading(fileKey);
    try {
      const body = { file_key: fileKey };
      const t = (target || "").trim();
      if (t) body.target_category = t;
      const res = await fetch(`${API_URL}/upload/files/reprocess`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        await fetchFiles();
        // Refresh Athena tables so AI Insights sees the reprocessed data immediately
        fetch(`${API_URL}/ai/sync-tables`, { method: "POST", headers: { Authorization: `Bearer ${idToken}` } }).catch(() => {});
        notify("success", "File reprocessed", `${data.message}\nQuality: ${data.qualityControl?.status} · ${data.rows?.toLocaleString()} rows`);
      } else { const err = await res.json().catch(() => ({})); notify("error", "Couldn't reprocess", err.detail || "Please try again."); }
    } catch { notify("error", "Couldn't reprocess", "Something went wrong reprocessing this file. Please try again."); }
    finally { setActionLoading(null); }
  };

  // Move a file to another project (R33). The source is the active project (sent via
  // the X-Project-Id header); the destination is chosen from the picker. Re-cleans both.
  const moveFile = async (fileKey, targetProjectId, force = false) => {
    setMovingFile(null);
    const target = projects.find((p) => p.projectId === targetProjectId);
    const destName = target?.displayName || targetProjectId;
    if (!force) {
      const confirmed = await confirm({
        title: `Move this file to “${destName}”?`,
        message: "The file is re-cleaned and de-duplicated in the destination project and removed from this one. Earlier AI answers in BOTH projects may change.",
        confirmLabel: "Move file",
        cancelLabel: "Cancel",
        tone: "primary",
      });
      if (!confirmed) return;
    }
    setActionLoading(fileKey);
    setMoveBusy({ name: destName });

    const syncProject = (pid) => fetch(`${API_URL}/ai/sync-tables`, {
      method: "POST", headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": pid },
    });
    // After a successful move the source file no longer appears in THIS project's active
    // list. A LARGE file's move re-cleans/de-dupes server-side and can exceed the API
    // Gateway 30s limit — the browser then gets a 504 (or a dropped connection) even
    // though the Lambda finishes the move (it's crash-safe: a move journal + reconcile
    // on the next /files load rolls it forward). So on a timeout we VERIFY the real
    // outcome by polling /upload/files (which also DRIVES that reconciliation) before
    // ever reporting failure — turning a false "Failed to move file" into accurate status.
    const verifyMoved = async (tries = 12, delayMs = 3000) => {
      for (let i = 0; i < tries; i++) {
        await new Promise((r) => setTimeout(r, delayMs));
        try {
          const token = await getFreshToken() || idToken;
          const res = await fetch(`${API_URL}/upload/files`, { headers: { Authorization: `Bearer ${token}`, "X-Project-Id": activeProjectId } });
          if (res.ok) {
            const data = await res.json();
            const stillHere = (data.files || []).some((f) => f.key === fileKey);
            if (!stillHere) return true; // source gone → move committed & reconciled
          }
        } catch { /* keep polling through transient errors */ }
      }
      return false;
    };
    const finishSuccess = async (msg) => {
      await fetchFiles();
      try { await syncProject(targetProjectId); } catch { /* best-effort */ }
      syncProject(activeProjectId).catch(() => {});
      notify("success", "File moved", msg);
    };

    try {
      const res = await fetch(`${API_URL}/upload/files/move`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json", "X-Project-Id": activeProjectId },
        body: JSON.stringify({ file_key: fileKey, target_project_id: targetProjectId, force }),
      });
      if (res.ok) {
        const data = await res.json();
        // No-op move: every row already exists in the destination. Don't remove the
        // source silently — ask the user what they want to do.
        if (data.status === "already_present") {
          setMoveBusy(null); setActionLoading(null);
          const ok = await confirm({
            title: "Nothing new to move",
            message: `“${data.file_name}” wasn't moved — all of its data already exists in “${data.target_project_name}”, so it's still here in this project.\n\nRemove it from this project (its data stays in “${data.target_project_name}”), or keep it here?`,
            confirmLabel: "Remove from this project",
            cancelLabel: "Keep it here",
            tone: "warning",
          });
          if (ok) await moveFile(fileKey, targetProjectId, true); // re-run, this time removing the source
          return;
        }
        // fixed-visual-blueprint Task 28 (R14.4): a move INTO a blueprint-mode project surfaces
        // an unmapped/mapped summary in the move response (set by upload._unmapped_columns_notice,
        // Task 20) — non-blocking, informational, shown alongside the normal move success toast.
        await finishSuccess(data.unmapped_columns_notice ? `${data.message}\n\n${data.unmapped_columns_notice}` : data.message);
      } else {
        // Non-2xx (e.g. a 504 gateway timeout on a large move). The move may still be
        // completing server-side — verify before declaring failure.
        const moved = await verifyMoved();
        if (moved) await finishSuccess("File moved.");
        else { const err = await res.json().catch(() => ({})); notify("error", "Couldn't move file", err.detail || "Please try again."); }
      }
    } catch {
      // Network error / dropped connection (also how a gateway timeout can surface).
      // Verify the actual outcome before reporting failure.
      const moved = await verifyMoved();
      if (moved) await finishSuccess("File moved.");
      else notify("error", "Couldn't move file", "If it was a large file it may still be finishing — refresh in a moment.");
    } finally { setActionLoading(null); setMoveBusy(null); }
  };

  // Download the CLEANED data for a file (what was actually imported) — data-transparency.
  // The server returns a short-lived presigned S3 URL so the browser downloads directly
  // from S3 (no API-Gateway payload limit on large clean files).
  const downloadCleanFile = async (fileKey) => {
    setActionLoading(fileKey);
    try {
      const token = await getFreshToken() || idToken;
      const res = await fetch(`${API_URL}/upload/files/download-url`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", "X-Project-Id": activeProjectId },
        body: JSON.stringify({ file_key: fileKey }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); notify("error", "Download failed", e.detail || "We couldn't prepare the download. Please try again."); return; }
      const { url } = await res.json();
      const a = document.createElement("a");
      a.href = url; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { notify("error", "Download failed", "Something went wrong preparing the download. Please try again."); }
    finally { setActionLoading(null); }
  };

  const restoreFile = async (fileKey) => {
    // Inform user that restore re-processes the file
    const confirmed = await confirm({
      title: "Restore this file?",
      message: "Restoring re-processes it as a new upload: data is cleaned and normalized again, duplicate rows already in your active data are excluded, and only new/unique rows are added to your active dataset.",
      confirmLabel: "Restore file",
      cancelLabel: "Cancel",
      tone: "primary",
    });
    if (!confirmed) return;

    // Poll a restore job until it reaches a terminal status (roadmap #86). The re-ingest of a
    // large file runs in a background Lambda (past the 30s API Gateway cap), so we poll instead
    // of blocking on one long request. Budget matches the Lambda's REAL ceiling (300s, raised
    // from 60s alongside the large-file ingest fix — see backend_stack.py's ApiLambda timeout)
    // plus margin: a real 306K-row restore measured 126s end-to-end, which the OLD 120s budget
    // (40 tries x 3s, sized for a much smaller 97K-row reference file) missed by just 6 seconds —
    // the job had actually succeeded, but the customer saw a "still working" timeout message
    // instead of the real result. 110 tries x 3s = 330s covers the full 300s ceiling + margin.
    const pollRestoreStatus = async (jobId) => {
      const url = `${API_URL}/upload/files/restore-status/${encodeURIComponent(jobId)}`;
      for (let i = 0; i < 110; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const r = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
          if (r.ok) {
            const j = await r.json();
            if (j.status && j.status !== "restoring" && j.status !== "unknown") return j;
          }
        } catch { /* transient network — keep polling */ }
      }
      return { status: "timeout" };
    };

    // Keep the button in its spinning/disabled state for the WHOLE operation (submit + poll).
    setActionLoading(fileKey);
    try {
      const res = await fetch(`${API_URL}/upload/files/restore`, { method: "POST", headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" }, body: JSON.stringify({ file_key: fileKey }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        notify("error", "Couldn't restore", (typeof err.detail === "string" ? err.detail : "") || "Please try again.");
        return;
      }
      let data = await res.json();
      // Async: the server started a background restore and returned a job id — poll it to
      // completion so the user sees a real result (and the file auto-appears in Active) with no
      // manual Refresh. (Falls through directly if the server returned a terminal status inline.)
      if (data.status === "restoring" && data.job_id) {
        notify("info", "Restoring…", "Re-processing your file. This can take a few minutes for very large files.");
        data = await pollRestoreStatus(data.job_id);
      }
      if (data.status === "completed") {
        await fetchFiles();
        if (data.duplicates_removed > 0) {
          notify("success", "File restored", `${data.unique_rows} unique rows added (${data.duplicates_removed} duplicates excluded).`);
        } else {
          notify("success", "File restored", `${data.unique_rows ?? ""} rows re-processed and added to active data.`.trim());
        }
        // Sync Athena tables + anomaly scan so AI Insights reflects the restored data.
        fetch(`${API_URL}/ai/sync-tables`, { method: "POST", headers: { Authorization: `Bearer ${idToken}` } }).catch(() => {});
        fetch(`${API_URL}/ai/detect-anomalies`, { method: "POST", headers: { Authorization: `Bearer ${idToken}` } }).catch(() => {});
      } else if (data.status === "all_duplicates") {
        await fetchFiles();
        notify("info", "Nothing to restore", data.message || "All data in this file already exists in your active files.");
      } else if (data.status === "failed") {
        notify("error", "Couldn't restore", data.message || "Please try again.");
      } else {
        // timeout / unknown — the background job may still be finishing; refresh and inform.
        await fetchFiles();
        notify("info", "Still restoring", "This file is taking a while to re-process — it'll appear in Active shortly.");
      }
    } catch { notify("error", "Couldn't restore", "Something went wrong restoring this file. Please try again."); }
    finally { setActionLoading(null); }
  };

  // Handle user choice: Add to existing or Replace all
  const handleUploadChoice = async (choice) => {
    if (!pendingUpload) return;
    
    if (choice === 'replace') {
      // Archive all existing files in this category first
      setStatus(STATUS.REQUESTING);
      setMessage("Archiving existing files...");
      try {
        const res = await fetch(`${API_URL}/upload/files/archive-category`, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ category: pendingUpload.fileCategory })
        });
        if (!res.ok) {
          setStatus(STATUS.ERROR);
          setMessage("Failed to archive existing files.");
          setPendingUpload(null);
          return;
        }
        await fetchFiles();
      } catch (e) {
        setStatus(STATUS.ERROR);
        setMessage("Failed to archive existing files.");
        setPendingUpload(null);
        return;
      }
    }
    
    // Continue with the upload (either add or after replace)
    // Re-trigger processFile by calling it with the pending data
    setPendingUpload(null);
    setStatus(STATUS.REQUESTING);
    setMessage("Step 4/6 — 🤖 AI-powered column standardization...");
    // The processFile function will be re-invoked via the pendingUpload mechanism
    // For simplicity, we'll just reset and let the user re-drop the file
    // Actually, let's store enough to continue...
    setStatus(STATUS.IDLE);
    setMessage(choice === 'replace' ? "Existing files archived. Please re-upload your file." : "");
    reset();
  };

  // `uploadProjectId` — the project this file must be uploaded/imported INTO. Defaults to the
  // currently-active project (single-file drops), but processBatch passes the project CAPTURED at
  // batch start, so every request for this file is pinned to it and a mid-batch project switch can
  // never re-route an in-flight file to the wrong project (see api.js getPresignedUrl/ingestFile).
  const processFile = useCallback(async (file, uploadProjectId = activeProjectId) => {
    const MAX_SIZE_MB = 100, MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    const ALLOWED = ['.csv', '.xlsx', '.xls'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED.includes(ext)) { setStatus(STATUS.ERROR); setMessage(`"${file.name}" isn't a supported file type. Supported file types: .csv, .xlsx (modern Excel), and .xls (legacy Excel).`); setFileName(file.name); return { ok: false, message: "not a CSV or Excel file" }; }
    if (file.size === 0) { setStatus(STATUS.ERROR); setMessage("File is empty."); setFileName(file.name); return { ok: false, message: "file is empty" }; }
    if (file.size < 100) { setStatus(STATUS.ERROR); setMessage("File too small — likely empty or headers only."); setFileName(file.name); return { ok: false, message: "too small — likely headers only" }; }
    if (file.size > MAX_SIZE_BYTES) { setStatus(STATUS.ERROR); setMessage(`File exceeds ${MAX_SIZE_MB}MB limit.`); setFileName(file.name); return { ok: false, message: `exceeds ${MAX_SIZE_MB}MB limit` }; }

    // Convert Excel to CSV if needed
    let csvFile = file;
    let excelSheets = null; // Array of {name, csvFile} if Excel with multiple sheets
    if (ext === '.xlsx' || ext === '.xls') {
      setStatus(STATUS.HASHING); setMessage("Reading Excel file..."); setFileName(file.name);
      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        
        // Filter sheets that have actual data (at least 2 rows — header + 1 data row)
        const dataSheets = workbook.SheetNames.filter(name => {
          const sheet = workbook.Sheets[name];
          const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
          return (range.e.r - range.s.r) >= 1; // At least 2 rows
        });
        
        if (dataSheets.length === 0) {
          setStatus(STATUS.ERROR); setMessage("Excel file has no sheets with data.");
          return { ok: false, message: "no sheets with data" };
        }
        
        if (dataSheets.length === 1) {
          // Single sheet — treat as regular CSV upload
          const csvContent = XLSX.utils.sheet_to_csv(workbook.Sheets[dataSheets[0]]);
          csvFile = new File([csvContent], file.name.replace(/\.xlsx?$/i, '.csv'), { type: 'text/csv' });
        } else {
          // Multiple sheets — reject (Option 2). One sheet per file keeps the server the
          // single authority for parsing/cleaning/dedup (no client-side SheetJS path, no
          // Add/Replace prompt). Multi-sheet workbooks are rare for this data; ask the user
          // to split them so each sheet is classified and deduped on its own.
          setStatus(STATUS.ERROR);
          setMessage(
            `This workbook has ${dataSheets.length} sheets with data (${dataSheets.join(', ')}). ` +
            `Please upload one sheet per file — save each sheet as its own .csv or .xlsx and upload them separately.`
          );
          setFileName(file.name);
          return { ok: false, message: "multiple sheets — upload one sheet per file" };
        }
      } catch (e) {
        setStatus(STATUS.ERROR); setMessage("Couldn't read this Excel file — it may be corrupted, password-protected, or in a format we don't support (e.g. Excel 4/5 macro sheets or .xlsb). Supported file types: .csv, .xlsx (modern Excel), and .xls (legacy Excel).");
        return { ok: false, message: "couldn't read Excel file" };
      }
    }

    setStatus(STATUS.HASHING); setMessage("Step 1/6 — Computing file fingerprint..."); setFileName(file.name); setProgress(0);
    try {
      // For multi-sheet Excel, process each sheet sequentially
      const sheetsToProcess = excelSheets ? excelSheets : [{ name: null, csvFile, rowCount: 0 }];
      const parentFileId = excelSheets ? `excel-${Date.now()}` : null;
      let allReports = [];

      for (let sheetIdx = 0; sheetIdx < sheetsToProcess.length; sheetIdx++) {
        const currentSheet = sheetsToProcess[sheetIdx];
        const currentCsvFile = currentSheet.csvFile;
        const sheetLabel = excelSheets ? ` (Sheet ${sheetIdx + 1}/${sheetsToProcess.length}: "${currentSheet.name}")` : '';

        if (excelSheets) {
          setMessage(`Step 1/6 — Processing sheet "${currentSheet.name}"${sheetLabel}...`);
        }

      // Hash is computed on the CSV content to detect true duplicates
      const hash = await computeFileHash(currentCsvFile);
      
      // Read CSV headers and sample rows for classification
      setStatus(STATUS.REQUESTING); setMessage(`Step 2/6 — Analyzing file headers...${sheetLabel}`);
      let fileCategory = "general_data";
      let columnMapping = null;
      let fillDefaults = {};
      let requiredColumns = [];
      let fullSchema = []; // All known columns for this category (for consistent SPICE output)
      let numericColumns = []; // Columns that should be 0 if empty/invalid
      try {
        const text = await currentCsvFile.slice(0, 8192).text(); // Read first 8KB for headers
        const lines = text.split('\n').filter(line => line.trim().length > 0);
        
        // Find the header row — skip title rows (lines where most columns are empty)
        let headerLineIdx = -1;
        for (let i = 0; i < Math.min(lines.length, 5); i++) {
          const cols = lines[i].split(',');
          // Count non-empty columns, ignoring trailing empty commas
          const nonEmptyCols = cols.filter(c => c.trim().length > 0);
          // A header row needs at least 3 non-empty columns.
          // Don't use cols.length as denominator — trailing commas inflate it.
          if (nonEmptyCols.length >= 3) {
            headerLineIdx = i;
            break;
          }
        }
        
        if (headerLineIdx >= 0) {
          const rawHeaders = lines[headerLineIdx].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
          // Filter out empty headers (trailing commas in CSV create empty columns)
          const headers = rawHeaders.filter(h => h.length > 0);
          // Get 2-3 sample data rows after the header
          const sampleRows = lines.slice(headerLineIdx + 1, headerLineIdx + 4)
            .map(line => line.split(',').map(c => c.trim().replace(/^["']|["']$/g, '')));
          
          setMessage(`Step 3/6 — 🤖 Applying AI to classify file type (${headers.length} columns detected)...`);
          
          if (headers.length >= 2) {
            const classification = await classifyFile(headers, file.name, sampleRows.length > 0 ? sampleRows : null, uploadProjectId);
            fileCategory = classification.category;
            columnMapping = classification.column_mapping || null;
            fillDefaults = classification.fill_defaults || {};
            requiredColumns = classification.required_columns || [];
            fullSchema = classification.full_schema || [];
            numericColumns = classification.numeric_columns || [];
            
            // Show match info to the user
            const matchRatio = classification.match_ratio || 100;
            if (classification.new_columns && classification.new_columns.length > 0) {
              const newCols = classification.new_columns.join(', ');
              setMessage(`Step 3/6 — ✅ ${matchRatio}% match with "${classification.category}". New columns: ${newCols}`);
            } else if (matchRatio === 100) {
              setMessage(`Step 3/6 — ✅ 100% match with "${classification.category}"`);
            } else {
              setMessage(`Step 3/6 — ✅ ${matchRatio}% match with "${classification.category}"`);
            }
          }
        }
      } catch (classErr) {
        console.warn("Classification failed", classErr);
        setStatus(STATUS.ERROR);
        setMessage("Unable to analyze file structure. Please try again.");
        return { ok: false, message: "couldn't analyze file structure" };
      }

      // Brief pause so user can read the classification result
      await new Promise(r => setTimeout(r, 800));
      
      // Category already has files → what to do. On the server-ingest path we DON'T ask:
      // the server always validates duplicates and adds only genuinely-new rows, so an
      // "Add / Replace / Cancel" prompt is meaningless (and the old "Add" path didn't
      // actually proceed). Just fall through to ingest and let dedup decide. The prompt
      // only remains for the legacy client-side path (flag off).
      const existingFilesInCategory = activeFiles.filter(f => f.category === fileCategory);
      if (!serverSideIngest && existingFilesInCategory.length > 0) {
        // Save all pending data and ask user what to do
        setPendingUpload({ csvFile: currentCsvFile, file, fileCategory, columnMapping, fillDefaults, requiredColumns, fullSchema, numericColumns, hash, sheetLabel, excelSheets, sheetsToProcess, sheetIdx, allReports, currentSheet });
        setStatus(STATUS.CHOOSE_ACTION);
        setMessage(`Category "${fileCategory}" already has ${existingFilesInCategory.length} file(s). What would you like to do?`);
        return; // Wait for user choice
      }

      // ─── Server-authoritative ingest (Option B, flag `server_side_ingest`) ──────
      // Upload ONLY the raw original; the server classifies, parses Excel with openpyxl
      // (no SheetJS date shift), cleans, per-project dedups, writes the clean file, and
      // finalizes the FileLog + report. This is the SAME core the move path uses, so
      // upload and move can never diverge. Multi-sheet Excel still uses the client path
      // below (handled server-side in a fast-follow).
      if (serverSideIngest && !excelSheets) {
        let presigned;
        setStatus(STATUS.REQUESTING); setMessage(`Step 4/5 — Checking for duplicates...${sheetLabel}`);
        try {
          // Send the ORIGINAL file name (keeps the extension so the server detects Excel)
          // and the file's own size; `hash` already fingerprints the file for the dup check.
          presigned = await getPresignedUrl(file.name, "raw_data", hash, file.size, fileCategory, uploadProjectId);
        } catch (err) {
          if (err.response?.status === 409) {
            let detail = err.response?.data?.detail || "", parsed = {};
            try { parsed = typeof detail === "string" ? JSON.parse(detail) : detail; } catch { parsed = { reason: "duplicate", message: detail }; }
            setStatus(STATUS.DUPLICATE); setMessage(parsed.message || "Duplicate file."); setArchivedFileKey(parsed.reason === "archived" ? parsed.file_key : null); return { ok: false, dup: true, message: parsed.message || "already in your data" };
          }
          if (err.response?.status === 422) { const d = err.response.data?.detail; const msg = Array.isArray(d) ? d.map(x => x.msg).join('. ') : (d || "Validation failed."); setStatus(STATUS.ERROR); setMessage(msg); return { ok: false, message: msg }; }
          throw err;
        }
        // Upload only the RAW original to S3.
        setStatus(STATUS.UPLOADING); setMessage(`Step 4/5 — Uploading file...${sheetLabel}`);
        await uploadToS3(presigned.raw_upload_url, file, setProgress);
        // Server cleans + dedups from the raw and finalizes.
        setStatus(STATUS.UPLOADING); setMessage(`Step 5/5 — Cleaning & de-duplicating on the server...${sheetLabel}`);
        // A LARGE raw file (>=5MB on S3, same gate the server's fast Excel extractor uses) now
        // routes through a BACKGROUND self-invocation server-side and /upload/ingest returns
        // immediately with {status: "ingesting", job_id} instead of blocking the request — so
        // the client polls GET /upload/ingest-status/{job_id} to a terminal state, mirroring
        // restoreFile's pollRestoreStatus below. This REPLACES the old timeout-guess fallback
        // (verifyIngested, which polled /upload/files hoping the log_id showed up and could
        // never distinguish "still running" from "genuinely failed") with a real, server-
        // reported outcome — including an honest "failed" if the background worker hits its
        // own hard timeout, instead of a silent/ambiguous result. Budget: 110 tries x 3s = 330s,
        // matching the Lambda's real 300s timeout + margin (see pollRestoreStatus's comment —
        // the OLD 120s default here would have hit the SAME 6-seconds-too-early timeout gap a
        // real 306K-row restore just proved out live).
        const pollIngestStatus = async (jobId, tries = 110, delayMs = 3000) => {
          for (let i = 0; i < tries; i++) {
            await new Promise((r) => setTimeout(r, delayMs));
            try {
              const token = await getFreshToken() || idToken;
              const res = await fetch(`${API_URL}/upload/ingest-status/${encodeURIComponent(jobId)}`, {
                headers: { Authorization: `Bearer ${token}`, "X-Project-Id": uploadProjectId },
              });
              if (res.ok) {
                const data = await res.json();
                if (data.status && data.status !== "ingesting" && data.status !== "unknown") return data;
              }
            } catch { /* transient network — keep polling */ }
          }
          return { status: "timeout" };
        };
        let result;
        try {
          result = await ingestFile(presigned.log_id, uploadProjectId);
        } catch (err) {
          const st = err.response?.status;
          const timedOut = !err.response || st === 503 || st === 504 || st === 502;
          if (timedOut) {
            // A genuine network-level timeout despite the async conversion (e.g. a very slow
            // self-invoke dispatch or a transient API Gateway hiccup) — fall back to checking
            // /upload/files directly, since we have no job_id to poll in this case.
            setStatus(STATUS.UPLOADING);
            setMessage("Large file — finishing on the server. This can take up to a minute…");
            const verifyIngestedFallback = async (logId, tries = 18, delayMs = 4000) => {
              for (let i = 0; i < tries; i++) {
                await new Promise((r) => setTimeout(r, delayMs));
                try {
                  const token = await getFreshToken() || idToken;
                  const res = await fetch(`${API_URL}/upload/files`, { headers: { Authorization: `Bearer ${token}`, "X-Project-Id": uploadProjectId } });
                  if (res.ok) {
                    const data = await res.json();
                    if ((data.files || []).some((f) => (f.key || "").includes(logId))) return true;
                  }
                } catch { /* keep polling */ }
              }
              return false;
            };
            const landed = await verifyIngestedFallback(presigned.log_id);
            if (landed) {
              setStatus(STATUS.DONE);
              setMessage("✅ Done! This file was large, so it finished processing on the server and imported successfully.");
              fetchFiles();
              try {
                await fetch(`${API_URL}/ai/sync-tables`, { method: "POST", headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": uploadProjectId } });
                fetch(`${API_URL}/ai/detect-anomalies`, { method: "POST", headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": uploadProjectId } }).catch(() => {});
              } catch (e) { console.warn("Athena sync skipped", e); }
            } else {
              setStatus(STATUS.ERROR);
              setMessage("Your file is large and is still being processed on the server. Give it a minute, then click Refresh in Active Files to confirm it imported — you don't need to re-upload.");
            }
            return landed ? { ok: true, large: true } : { ok: false, pending: true, message: "large file — still finishing on the server" };
          }
          if (st === 413) {
            const d = err.response?.data?.detail;
            setStatus(STATUS.ERROR);
            setMessage(d || "This file has too many rows to process in one upload. Please split it into smaller files.");
            return { ok: false, message: d };
          }
          if (st === 422) {
            const d = err.response?.data?.detail;
            const msg = Array.isArray(d) ? d.map((x) => x.msg).join('. ') : (d || "We couldn't process this file.");
            setStatus(STATUS.ERROR);
            setMessage(msg);
            return { ok: false, message: msg };
          }
          const emsg = err.response?.data?.detail || err.message || "The server couldn't process this file. Please try again.";
          setStatus(STATUS.ERROR);
          setMessage(emsg);
          return { ok: false, message: emsg };
        }

        // Large file: the server started a background ingest job and returned immediately.
        // Poll it to a terminal status so the user sees a real result (success OR an honest
        // failure — e.g. the background worker hit its own timeout) instead of guessing.
        if (result.status === "ingesting" && result.job_id) {
          setStatus(STATUS.UPLOADING);
          setMessage("Large file — processing on the server. This can take a minute or two…");
          const jobResult = await pollIngestStatus(result.job_id);
          if (jobResult.status === "success" || jobResult.status === "completed") {
            result = jobResult;
          } else if (jobResult.status === "failed") {
            setStatus(STATUS.ERROR);
            setMessage(jobResult.message || "The file could not be processed. Please try again.");
            return { ok: false, message: jobResult.message };
          } else {
            // "timeout" (our own poll budget ran out) — the job may still be genuinely running
            // past our ~2min poll window; tell the customer honestly instead of guessing.
            setStatus(STATUS.ERROR);
            setMessage("This is taking longer than expected. Give it a couple more minutes, then click Refresh in Active Files to check if it imported — you don't need to re-upload yet.");
            return { ok: false, pending: true, message: "ingest job still running past the poll window" };
          }
        }

        const rep = result.report || {};
        const imported = result.rows_imported || 0;
        const platformSuffix = result.platform_text ? ` · ${result.platform_text}` : '';
        const dups = result.duplicates_removed || 0;
        const dropped = rep.droppedRows || 0;
        setStatus(STATUS.DONE);
        if (imported === 0) {
          setMessage(`⚠️ ${result.message || "No new data imported."}` +
            (result.original_rows ? ` · ${result.original_rows} rows analyzed` : '') +
            (dups > 0 ? ` · ${dups} duplicates` : '') +
            (dropped > 0 ? ` · ${dropped} invalid removed` : '') + platformSuffix);
        } else {
          setMessage(`✅ Done! ${imported.toLocaleString()} rows imported` +
            (dropped > 0 ? ` · ${dropped} invalid removed` : '') +
            (dups > 0 ? ` · ${dups} duplicates skipped` : '') +
            ((rep.phonesNormalized || 0) > 0 ? ` · ${rep.phonesNormalized} phones normalized` : '') +
            ((rep.datesNormalized || 0) > 0 ? ` · ${rep.datesNormalized} dates standardized` : '') +
            ((rep.emailsNormalized || 0) > 0 ? ` · ${rep.emailsNormalized} emails normalized` : '') +
            ((rep.emptyFieldsFilled || 0) > 0 ? ` · ${rep.emptyFieldsFilled} fields filled` : '') +
            ((rep.columnsStandardized || 0) > 0 ? ` · ${rep.columnsStandardized} columns standardized` : '') +
            platformSuffix);
        }
        fetchFiles();
        // Sync Athena tables so AI Insights can query the new data, then anomaly scan. Pinned to
        // the upload's project so a mid-batch switch can't sync/scan the wrong project's tables.
        try {
          await fetch(`${API_URL}/ai/sync-tables`, { method: "POST", headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": uploadProjectId } });
          fetch(`${API_URL}/ai/detect-anomalies`, { method: "POST", headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": uploadProjectId } }).catch(() => {});
        } catch (e) { console.warn("Athena sync skipped", e); }
        return { ok: true, rows: imported, dups, dropped, noNew: imported === 0 };
      }

      setMessage("Step 4/6 — 🤖 AI-powered column standardization...");
      
      // ─── Data Normalization Functions ───────────────────────────────────
      const normalizePhone = (val) => {
        if (!val) return val;
        // Remove all non-digit characters
        let digits = val.replace(/\D/g, '');
        // Remove leading country code "1" for US numbers (if 11 digits)
        if (digits.length === 11 && digits.startsWith('1')) {
          digits = digits.substring(1);
        }
        // Format as (XXX) XXX-XXXX if 10 digits
        if (digits.length === 10) {
          return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
        }
        // Return cleaned digits if not standard length
        return digits || val;
      };

      const normalizeEmail = (val) => {
        if (!val) return val;
        return val.trim().toLowerCase();
      };

      const normalizeDate = (val) => {
        if (!val) return val;
        // Try to parse and output as YYYY-MM-DD
        const parsed = new Date(val);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString().split('T')[0];
        }
        return val;
      };

      const normalizeZipCode = (val) => {
        if (!val) return val;
        // Keep only digits, pad to 5 if shorter
        const digits = val.replace(/\D/g, '');
        if (digits.length <= 5) return digits.padStart(5, '0');
        // ZIP+4 format
        if (digits.length === 9) return `${digits.slice(0,5)}-${digits.slice(5)}`;
        return digits;
      };

      const normalizeCurrency = (val) => {
        if (!val) return val;
        // Remove $, commas, spaces — keep the number
        const cleaned = val.replace(/[$,\s]/g, '');
        const num = parseFloat(cleaned);
        return isNaN(num) ? val : num.toFixed(2);
      };

      const normalizeState = (val) => {
        if (!val) return val;
        // Uppercase state abbreviations
        const trimmed = val.trim();
        if (trimmed.length === 2) return trimmed.toUpperCase();
        return trimmed;
      };

      // Detect which normalizer to apply based on column name
      const getNormalizer = (colName) => {
        const name = colName.toLowerCase();
        if (name.includes('phone') || name.includes('mobile') || name.includes('fax') || name.includes('tel')) return normalizePhone;
        if (name.includes('email') || name.includes('e_mail')) return normalizeEmail;
        if (name.includes('date') || name.includes('created_at') || name.includes('updated_at')) return normalizeDate;
        if (name.includes('zip') || name.includes('postal')) return normalizeZipCode;
        if (name.includes('price') || name.includes('amount') || name.includes('cost') || name.includes('value') || name.includes('revenue')) return normalizeCurrency;
        if (name === 'state' || name.includes('_state')) return normalizeState;
        return null;
      };
      // ─── End Normalization Functions ────────────────────────────────────

      // Clean the file: rename columns + fill defaults + normalize data
      let fileToUpload = currentCsvFile;
      let cleaningReport = null;
      // Always run the cleaning pipeline. Even when AI classification returned no
      // column_mapping (e.g. it fell back to "general_data"), we derive snake_case
      // names per-header below — files must NEVER be uploaded raw, which previously
      // left garbage rows + messy headers + no schema and orphaned the data.
      if (!columnMapping) columnMapping = {};
      {
        try {
          const fullText = await currentCsvFile.text();
          const allLines = fullText.split('\n');
          
          // CSV-aware field splitter (handles quoted fields with commas)
          const parseCSVLine = (line) => {
            const fields = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
              const ch = line[i];
              if (ch === '"') {
                if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
                  current += '"'; i++; // Escaped quote
                } else {
                  inQuotes = !inQuotes;
                }
              } else if (ch === ',' && !inQuotes) {
                fields.push(current);
                current = '';
              } else {
                current += ch;
              }
            }
            fields.push(current);
            return fields;
          };
          
          // Find header row index again in full file
          let hdrIdx = -1;
          for (let i = 0; i < Math.min(allLines.length, 5); i++) {
            const cols = parseCSVLine(allLines[i]);
            const nonEmpty = cols.filter(c => c.trim().length > 0);
            if (nonEmpty.length >= 3) {
              hdrIdx = i;
              break;
            }
          }
          
          if (hdrIdx >= 0) {
            // Parse headers
            const originalHeaders = parseCSVLine(allLines[hdrIdx]).map(h => h.trim().replace(/^["']|["']$/g, ''));
            
            // Build mapping: original header → clean name
            const cleanHeaders = originalHeaders.map(h => {
              if (!h.trim()) return '';
              return columnMapping[h] || h.toLowerCase().replace(/[^a-z0-9]/g, '_');
            });
            
            // Determine output columns: use fullSchema if available (ensures consistent SPICE schema)
            // This guarantees all files in the same category have identical columns
            const outputColumns = fullSchema.length > 0 ? fullSchema : cleanHeaders.filter(h => h.trim());
            
            // Build a lookup: clean column name → index in the original file
            const cleanToOrigIdx = {};
            cleanHeaders.forEach((ch, idx) => { if (ch.trim()) cleanToOrigIdx[ch] = idx; });
            
            // Build normalizers for output columns
            const outputNormalizers = outputColumns.map(h => getNormalizer(h));
            
            // Build numeric column set for fast lookup
            const numericSet = new Set(numericColumns);
            
            const cleanLines = [outputColumns.join(',')];
            
            // Identify required column indices for garbage row detection
            const requiredIndices = requiredColumns.map(rc => outputColumns.indexOf(rc)).filter(i => i >= 0);
            let rowsDropped = 0;
            let totalRows = 0;
            let phonesNormalized = 0;
            let datesNormalized = 0;
            let emailsNormalized = 0;
            let emptyFieldsFilled = 0;
            let internalDupsRemoved = 0;
            let crossFileDupsRemoved = 0;

            // ── Integrity/sanity accumulators (roadmap #72) ──────────────────
            // Deterministic pre/post reconciliation; the verdict itself is computed
            // by the backend (single-source thresholds). Dedup/garbage removals are
            // tracked as EXPECTED (removedNumericValue) and never counted as distortion.
            const numRawSum = {}, numCleanSum = {}, numCoercedCount = {}, numCoercedLoss = {};
            const removedNumericValue = {}, txtRawPop = {}, txtCleanPop = {};
            const toNumber = (s) => {
              if (s === null || s === undefined) return null;
              const t = String(s).replace(/[\s\u00A0\u2000-\u200F]+/g, ' ').trim();
              if (!t) return null;
              const n = parseFloat(t.replace(/[$€£¥,\s\u00A0]/g, ''));
              return isFinite(n) ? n : null;
            };
            const attributeRemoved = (rawNumByCol) => {
              for (const c in rawNumByCol) {
                const n = rawNumByCol[c];
                if (n) removedNumericValue[c] = (removedNumericValue[c] || 0) + n;
              }
            };

            // Build dedup sets
            const seenRowHashes = new Set(); // Level 1: within this file
            let existingRowSet = new Set(); // Level 2: across existing files
            
            // Always fetch existing rows from S3 for cross-file dedup
            // (Don't rely on frontend activeFiles state — it may not be refreshed yet)
            try {
              setMessage("Step 5/6 — Loading existing data for deduplication...");
              const dedupRes = await fetch(`${API_URL}/upload/existing-rows/${encodeURIComponent(fileCategory)}`, {
                headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": uploadProjectId }
              });
              if (dedupRes.ok) {
                const dedupData = await dedupRes.json();
                if (dedupData.count > 0) {
                  existingRowSet = new Set(dedupData.rows || []);
                }
              }
            } catch (e) { console.warn("Failed to fetch existing rows for dedup", e); }
            
            setMessage("Step 5/6 — 🤖 AI-powered data normalization + deduplication...");
            
            for (let i = hdrIdx + 1; i < allLines.length; i++) {
              if (!allLines[i].trim()) continue;
              totalRows++;
              const cols = parseCSVLine(allLines[i]);
              const rawByCol = {};     // outCol -> raw cell (pre-clean)
              const rawNumByCol = {};  // outCol -> parsed raw number (numeric cols)
              
              // Build output row using the full schema columns
              const cleanCols = outputColumns.map((outCol, colIdx) => {
                // Find the value from the original file
                const origIdx = cleanToOrigIdx[outCol];
                let cleanVal = origIdx !== undefined ? (cols[origIdx] || '').trim() : '';
                rawByCol[outCol] = cleanVal;  // capture raw before any transformation
                if (numericSet.has(outCol)) rawNumByCol[outCol] = toNumber(cleanVal);
                
                // For numeric columns: clean to valid number or 0
                if (numericSet.has(outCol)) {
                  if (!cleanVal) {
                    cleanVal = '0';
                  } else {
                    // Normalize: strip ALL whitespace variants (including non-breaking spaces from Excel)
                    const normalizedVal = cleanVal.replace(/[\s\u00A0\u2000-\u200F]+/g, ' ').trim();
                    // Reject values that look like phone numbers or IDs (not actual monetary values)
                    const looksLikePhone = /[\(\)]/.test(normalizedVal) || /^\d{10,}$/.test(normalizedVal.replace(/\D/g, ''));
                    if (looksLikePhone) {
                      cleanVal = '0';
                    } else {
                      // Remove currency symbols, commas, ALL whitespace — keep decimal and minus
                      const numStr = normalizedVal.replace(/[$€£¥,\s\u00A0]/g, '');
                      const num = parseFloat(numStr);
                      if (isNaN(num) || !isFinite(num)) {
                        cleanVal = '0';
                      } else {
                        cleanVal = num.toFixed(2);
                      }
                    }
                  }
                } else {
                  // Fill defaults for empty non-numeric values
                  if (!cleanVal && fillDefaults[outCol]) {
                    cleanVal = fillDefaults[outCol];
                    emptyFieldsFilled++;
                  }
                }
                
                // Apply normalizer (phone, email, date, zip, etc.)
                if (cleanVal && outputNormalizers[colIdx]) {
                  const original = cleanVal;
                  cleanVal = outputNormalizers[colIdx](cleanVal);
                  if (cleanVal !== original) {
                    if (outCol.includes('phone') || outCol.includes('mobile') || outCol.includes('tel')) phonesNormalized++;
                    else if (outCol.includes('email')) emailsNormalized++;
                    else if (outCol.includes('date') || outCol.includes('created') || outCol.includes('updated')) datesNormalized++;
                  }
                }
                return cleanVal;
              });
              
              // Garbage row detection:
              const filledCount = cleanCols.filter(c => c && c.trim() && c !== 'Not Provided' && c !== 'Unknown').length;
              if (filledCount < outputColumns.length * 0.3) { rowsDropped++; attributeRemoved(rawNumByCol); continue; }
              
              if (requiredIndices.length > 0) {
                const hasAnyRequired = requiredIndices.some(ri => cleanCols[ri] && cleanCols[ri].trim() && cleanCols[ri] !== 'Not Provided');
                if (!hasAnyRequired) { rowsDropped++; attributeRemoved(rawNumByCol); continue; }
              }
              
              // Row deduplication (Level 1: within file, Level 2: across existing files)
              const rowStr = cleanCols.join(',');
              if (seenRowHashes.has(rowStr)) { internalDupsRemoved++; attributeRemoved(rawNumByCol); continue; }
              if (existingRowSet.has(rowStr)) { crossFileDupsRemoved++; attributeRemoved(rawNumByCol); continue; }
              seenRowHashes.add(rowStr);

              // KEPT row → accumulate integrity metrics
              outputColumns.forEach((outCol, colIdx) => {
                const cv = cleanCols[colIdx];
                if (numericSet.has(outCol)) {
                  const rn = rawNumByCol[outCol];
                  const cn = parseFloat(cv);
                  const cnv = isFinite(cn) ? cn : 0;
                  if (rn !== null && rn !== undefined) numRawSum[outCol] = (numRawSum[outCol] || 0) + rn;
                  numCleanSum[outCol] = (numCleanSum[outCol] || 0) + cnv;
                  if (rn !== null && rn !== undefined && Math.abs(rn) > 1e-9 && Math.abs(cnv) < 1e-9) {
                    numCoercedCount[outCol] = (numCoercedCount[outCol] || 0) + 1;
                    numCoercedLoss[outCol] = (numCoercedLoss[outCol] || 0) + rn;
                  }
                } else {
                  const rv = rawByCol[outCol];
                  if (rv && rv !== 'Not Provided' && rv !== 'Unknown') txtRawPop[outCol] = (txtRawPop[outCol] || 0) + 1;
                  if (cv && cv !== 'Not Provided' && cv !== 'Unknown') txtCleanPop[outCol] = (txtCleanPop[outCol] || 0) + 1;
                }
              });
              
              // Quote fields that contain commas for valid CSV output
              const csvRow = cleanCols.map(v => v.includes(',') ? `"${v.replace(/"/g, '""')}"` : v).join(',');
              cleanLines.push(csvRow);
            }
            
            // Build cleaning report
            cleaningReport = {
              originalRows: totalRows,
              importedRows: cleanLines.length - 1, // minus header row
              droppedRows: rowsDropped,
              internalDuplicates: internalDupsRemoved,
              crossFileDuplicates: crossFileDupsRemoved,
              columnsStandardized: outputColumns.length,
              phonesNormalized,
              datesNormalized,
              emailsNormalized,
              emptyFieldsFilled
            };

            // ─── Quality-control verdict (per-file QC log) ───────────────────
            // Records whether the file passed each stage of the pipeline so we have
            // an at-a-glance quality status per file. PASS requires a real category
            // (not the general_data fallback) + cleaning + normalization + dedup.
            const qcClassified = !!fileCategory && fileCategory !== 'general_data';
            const qcNormalized = (phonesNormalized + datesNormalized + emailsNormalized + emptyFieldsFilled) > 0
              || outputColumns.length > 0; // normalization stage ran across the output columns
            const qcIssues = [];
            if (!qcClassified) qcIssues.push('Classified as the "general_data" fallback — no specific category was detected');
            const qc = {
              classified: qcClassified,
              cleaned: true,            // the cleaning pipeline ran (we always clean now)
              normalized: qcNormalized,
              deduplicated: true,       // internal + cross-file dedup ran
              issues: qcIssues,
              status: qcClassified ? 'PASS' : 'PARTIAL',  // cleaning ran; fallback category => PARTIAL
            };
            cleaningReport.qualityControl = qc;

            // ─── Integrity/sanity verdict (roadmap #72) ──────────────────────
            // Send the accumulated reconciliation metrics to the backend, which owns
            // the verdict logic (PASS/WARN/BLOCK). Best-effort — never blocks on error.
            try {
              const ivRes = await fetch(`${API_URL}/upload/integrity-verdict`, {
                method: "POST",
                headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json", "X-Project-Id": uploadProjectId },
                body: JSON.stringify({
                  total_rows: totalRows,
                  imported: cleanLines.length - 1,
                  rows_dropped: rowsDropped,
                  internal_dups: internalDupsRemoved,
                  cross_file_dups: crossFileDupsRemoved,
                  num_raw_sum: numRawSum, num_clean_sum: numCleanSum,
                  num_coerced_count: numCoercedCount, num_coerced_loss: numCoercedLoss,
                  removed_numeric_value: removedNumericValue,
                  txt_raw_pop: txtRawPop, txt_clean_pop: txtCleanPop,
                }),
              });
              if (ivRes.ok) cleaningReport.integrity = await ivRes.json();
            } catch (e) { console.warn("Integrity verdict unavailable", e); }
            
            if (rowsDropped > 0 || internalDupsRemoved > 0 || crossFileDupsRemoved > 0) {
              const parts = [];
              if (rowsDropped > 0) parts.push(`${rowsDropped} invalid rows removed`);
              if (internalDupsRemoved > 0) parts.push(`${internalDupsRemoved} internal duplicates`);
              if (crossFileDupsRemoved > 0) parts.push(`${crossFileDupsRemoved} cross-file duplicates`);
              setMessage(`Step 5/6 — 🧹 ${parts.join(' · ')}. Keeping ${cleanLines.length - 1} records.`);
              await new Promise(r => setTimeout(r, 800));
            }
            
            const cleanCsv = cleanLines.join('\n');
            fileToUpload = new File([cleanCsv], file.name, { type: 'text/csv' });
          }
        } catch (cleanErr) {
          // Do NOT silently upload the raw file — that's what created uncleaned,
          // unschema'd orphans. Surface it as a failed upload the user can retry.
          console.error("File cleaning failed", cleanErr);
          setStatus(STATUS.ERROR);
          setMessage("We couldn't process this file — it may be malformed or corrupted. Please check the file and try again.");
          return;
        }
      }

      // QC gate (idea #1): if the file didn't fully pass (e.g. it fell back to the
      // "general_data" category), warn before finalizing. The user can continue
      // (and Reprocess later to assign a category) or cancel.
      if (cleaningReport?.qualityControl && cleaningReport.qualityControl.status !== 'PASS') {
        const qc = cleaningReport.qualityControl;
        const proceed = await confirm({
          title: `Quality check — “${qc.status}”`,
          message:
            (qc.issues && qc.issues.length ? qc.issues.join('\n') + '\n\n' : '') +
            `It was still cleaned, normalized and deduplicated (${(cleaningReport.importedRows || 0).toLocaleString()} rows kept). You can upload anyway and Reprocess later to assign a proper category.`,
          confirmLabel: "Upload anyway",
          cancelLabel: "Stop",
          tone: "warning",
        });
        if (!proceed) {
          setStatus(STATUS.ERROR);
          setMessage('Upload cancelled. Tip: give the file clearer column headers, or upload it and use "Reprocess" to assign a category.');
          return;
        }
      }

      // Integrity/sanity gate (roadmap #72): if the pre/post reconciliation flagged
      // anything (e.g. a real number silently zeroed, unexpected data loss), show it
      // and require confirmation. WARN-only policy — the only hard stop is "0 rows".
      if (cleaningReport?.integrity && cleaningReport.integrity.verdict && cleaningReport.integrity.verdict !== 'PASS') {
        const iv = cleaningReport.integrity;
        if (iv.verdict === 'BLOCK') {
          setStatus(STATUS.ERROR);
          setMessage(iv.summary || 'Upload blocked: no valid rows remained after cleaning.');
          return;
        }
        const proceed = await confirm({
          title: "Data integrity check",
          message: `Please review before continuing:\n\n${iv.summary}`,
          confirmLabel: "Upload anyway",
          cancelLabel: "Stop",
          tone: "warning",
        });
        if (!proceed) {
          setStatus(STATUS.ERROR);
          setMessage('Upload cancelled after the integrity review. Check the flagged values in your file, then re-upload.');
          return;
        }
      }

      setMessage(`Step 6/6 — Checking for duplicates...${sheetLabel}`);
      let presigned;
      try { presigned = await getPresignedUrl(currentCsvFile.name, "raw_data", hash, currentCsvFile.size, fileCategory, uploadProjectId); }
      catch (err) {
        if (err.response?.status === 409) {
          let detail = err.response?.data?.detail || "", parsed = {};
          try { parsed = typeof detail === "string" ? JSON.parse(detail) : detail; } catch { parsed = { reason: "duplicate", message: detail }; }
          setStatus(STATUS.DUPLICATE); setMessage(parsed.message || "Duplicate file."); setArchivedFileKey(parsed.reason === "archived" ? parsed.file_key : null); return;
        }
        if (err.response?.status === 422) { const d = err.response.data?.detail; setStatus(STATUS.ERROR); setMessage(Array.isArray(d) ? d.map(x => x.msg).join('. ') : (d || "Validation failed.")); return; }
        throw err;
      }
      
      // Check if there are any rows to upload (after dedup + garbage removal)
      const importedRowCount = cleaningReport ? cleaningReport.importedRows : -1;
      
      if (importedRowCount === 0) {
        // All rows were duplicates or garbage — don't upload the clean file to S3
        setStatus(STATUS.UPLOADING); setMessage(`All rows already exist. Saving raw file for audit...${sheetLabel}`);
        // Still upload raw file (for audit trail)
        const rawFileToUpload = (sheetIdx === 0) ? file : currentCsvFile;
        await uploadToS3(presigned.raw_upload_url, rawFileToUpload, () => {});
        // Do NOT upload clean file — it has no data rows
        // Mark the file as "skipped" in DynamoDB so it doesn't appear in file list
        try {
          await fetch(`${API_URL}/upload/mark-skipped`, {
            method: "POST",
            headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json", "X-Project-Id": uploadProjectId },
            body: JSON.stringify({ log_id: presigned.log_id })
          });
        } catch (e) { console.warn("Failed to mark file as skipped", e); }
      } else {
        setStatus(STATUS.UPLOADING); setMessage(`Uploading clean file to S3...${sheetLabel}`);
        // Upload raw file (original Excel for first sheet, CSV for subsequent)
        const rawFileToUpload = (sheetIdx === 0) ? file : currentCsvFile;
        await uploadToS3(presigned.raw_upload_url, rawFileToUpload, () => {});
        // Upload clean file (always CSV, normalized, filtered — for QuickSight/Athena)
        await uploadToS3(presigned.upload_url, fileToUpload, excelSheets ? () => {} : setProgress);
      }
      
      // Save cleaning report to DynamoDB (always save — even for 0-row files, for audit)
      if (cleaningReport) {
        try {
          const reportHeaders = { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json", "X-Project-Id": uploadProjectId };
          const reportBody = { log_id: presigned.log_id, report: cleaningReport };
          if (parentFileId) reportBody.parent_file_id = parentFileId;
          if (currentSheet.name) reportBody.sheet_name = currentSheet.name;
          if (importedRowCount === 0) {
            reportBody.report.skipped = true; // Mark as no clean file created
            reportBody.skip_status_update = true; // Don't overwrite "skipped" status
          }
          await fetch(`${API_URL}/upload/report`, {
            method: "POST", headers: reportHeaders,
            body: JSON.stringify(reportBody)
          });
        } catch (e) { console.warn("Failed to save report", e); }
        allReports.push({ sheet: currentSheet.name, ...cleaningReport });
      }

      } // End of sheetsToProcess loop

      // Show final summary
      if (allReports.length > 0) {
        const totalImported = allReports.reduce((s, r) => s + (r.importedRows || 0), 0);
        const totalDropped = allReports.reduce((s, r) => s + (r.droppedRows || 0), 0);
        const totalPhones = allReports.reduce((s, r) => s + (r.phonesNormalized || 0), 0);
        const totalDates = allReports.reduce((s, r) => s + (r.datesNormalized || 0), 0);
        const totalEmails = allReports.reduce((s, r) => s + (r.emailsNormalized || 0), 0);
        const totalFilled = allReports.reduce((s, r) => s + (r.emptyFieldsFilled || 0), 0);
        // Columns are per-file schema width (same across sheets of one file) — show the max, not a sum
        const maxColumns = allReports.reduce((m, r) => Math.max(m, r.columnsStandardized || 0), 0);
        const totalInternalDups = allReports.reduce((s, r) => s + (r.internalDuplicates || 0), 0);
        const totalCrossFileDups = allReports.reduce((s, r) => s + (r.crossFileDuplicates || 0), 0);
        const totalOriginal = allReports.reduce((s, r) => s + (r.originalRows || 0), 0);
        const sheetsMsg = excelSheets ? ` from ${allReports.length} sheets` : '';
        
        if (totalImported === 0) {
          // All rows were duplicates — no clean file was created
          setStatus(STATUS.DONE);
          setMessage(`⚠️ No new data imported${sheetsMsg}` +
            ` · ${totalOriginal} rows analyzed` +
            (totalCrossFileDups > 0 ? ` · ${totalCrossFileDups} cross-file duplicates` : '') +
            (totalInternalDups > 0 ? ` · ${totalInternalDups} internal duplicates` : '') +
            (totalDropped > 0 ? ` · ${totalDropped} invalid removed` : '') +
            ` · 0 new rows`);
        } else {
          setStatus(STATUS.DONE);
          setMessage(`✅ Done! ${totalImported.toLocaleString()} rows imported${sheetsMsg}` +
            (totalDropped > 0 ? ` · ${totalDropped} invalid removed` : '') +
            (totalInternalDups > 0 ? ` · ${totalInternalDups} internal duplicates` : '') +
            (totalCrossFileDups > 0 ? ` · ${totalCrossFileDups} cross-file duplicates` : '') +
            (totalPhones > 0 ? ` · ${totalPhones} phones normalized` : '') +
            (totalDates > 0 ? ` · ${totalDates} dates standardized` : '') +
            (totalEmails > 0 ? ` · ${totalEmails} emails normalized` : '') +
            (totalFilled > 0 ? ` · ${totalFilled} fields filled` : '') +
            (maxColumns > 0 ? ` · ${maxColumns} columns standardized` : ''));
        }
      } else {
        setStatus(STATUS.DONE);
        setMessage("✅ File uploaded successfully.");
      }
      fetchFiles();
      // Sync Athena tables so AI Insights can query the new data. Pinned to the upload's project
      // so a mid-batch switch can't sync/scan the wrong project's tables.
      try {
        await fetch(`${API_URL}/ai/sync-tables`, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": uploadProjectId }
        });
        // Run anomaly detection on the new data
        fetch(`${API_URL}/ai/detect-anomalies`, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}`, "X-Project-Id": uploadProjectId }
        }).catch(() => {}); // Fire-and-forget — don't block the UI
      } catch (e) { console.warn("Athena sync skipped", e); }
    } catch (err) { setStatus(STATUS.ERROR); setMessage(err.response?.data?.detail || err.message || "Upload failed."); }
    // activeFiles + activeProjectId are in the deps so the "category already has files"
    // check always reflects the CURRENT project. Without them, processFile closed over a
    // stale file list after a project switch and could prompt "Files Already Exist" in a
    // project that is actually empty (showing another project's count).
  }, [fetchFiles, serverSideIngest, activeFiles, activeProjectId]);

  // Process several files ONE AT A TIME through the existing single-file pipeline, tracking a
  // per-file queue for the UI. Reuses processFile (which returns a per-file result) so the batch
  // path can never diverge from the single path. A failed/flagged file does NOT stop the rest.
  const processBatch = useCallback(async (files) => {
    // Capture the project ONCE, here, when the batch starts. Every file in this batch is pinned to
    // it (passed into processFile → the upload/ingest calls), so if the user switches projects
    // mid-batch the remaining files still land in the project they were dropped into — never the
    // newly-selected one. `batchProjectId` also lets the page show this queue only under its OWN
    // project (see the render guard), instead of implying files are going into whatever is now
    // selected. Both halves read the SAME captured id, so display and destination can't diverge.
    const uploadProjectId = activeProjectId;
    setBatchProjectId(uploadProjectId);
    setBatchDone(false);
    setStatus(STATUS.IDLE); setMessage(""); setFileName(null); setProgress(0);
    setBatchQueue(files.map((f) => ({ name: f.name, status: "queued", detail: "" })));
    for (let i = 0; i < files.length; i++) {
      setBatchQueue((prev) => prev && prev.map((it, idx) => idx === i ? { ...it, status: "active", detail: "cleaning & de-duplicating…" } : it));
      let r;
      try { r = await processFile(files[i], uploadProjectId); }
      catch (e) { r = { ok: false, message: "Unexpected error." }; }
      const st = r?.dup ? "dup" : (r?.ok ? "done" : "failed");
      const detail = r?.dup
        ? (r.message || "already in your data")
        : r?.ok
          ? (r.noNew ? "already in your data — nothing new"
             : r.large ? "imported (finished on the server)"
             : `${(r.rows || 0).toLocaleString()} rows imported${r.dups ? ` · ${r.dups} dupes skipped` : ""}${r.dropped ? ` · ${r.dropped} invalid removed` : ""}`)
          : (r?.message || "couldn't process — try uploading this file on its own");
      setBatchQueue((prev) => prev && prev.map((it, idx) => idx === i ? { ...it, status: st, detail } : it));
    }
    setStatus(STATUS.IDLE); setMessage(""); setProgress(0);
    setBatchDone(true);
    fetchFiles();
    // activeProjectId is a dep: processBatch reads it to CAPTURE the batch's project at start.
  }, [processFile, fetchFiles, activeProjectId, setBatchProjectId]);

  // Single file → the classic single-file flow. Multiple files → a SEQUENTIAL queue (one at a
  // time) via processBatch: dedup stays correct, the 30s gateway limit isn't raced, and Prod's
  // Lambda concurrency isn't saturated. (Parallel batch is a documented future improvement,
  // gated on raising the Lambda concurrency quota — see AWS_FOLLOWUPS.md.)
  const onDrop = useCallback((files) => {
    if (!files || files.length === 0) return;
    if (files.length === 1) processFile(files[0]);
    else processBatch(files);
  }, [processFile, processBatch]);
  // noClick/noKeyboard: the whole Files card is the drop target (getRootProps), but a
  // click on the card must NOT open the picker (rows, kebabs, links live inside it) —
  // the "＋ Add file" button opens the picker via open(). Same onDrop/drag handlers.
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({ onDrop, accept: { "text/csv": [".csv"], "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], "application/vnd.ms-excel": [".xls"] }, multiple: true, noClick: true, noKeyboard: true, disabled: status === STATUS.UPLOADING || status === STATUS.HASHING || !!batchQueue });
  const reset = () => { setStatus(STATUS.IDLE); setMessage(""); setFileName(null); setProgress(0); setArchivedFileKey(null); };

  const dzClass = `s3upload-dropzone ${isDragActive ? "active" : ""} ${status === STATUS.ERROR || (status === STATUS.DUPLICATE && !archivedFileKey) ? "error" : ""} ${status === STATUS.DONE ? "done" : ""} ${status === STATUS.UPLOADING ? "uploading" : ""}`;

  // An upload is in flight while we hash / classify / upload / server-ingest. Navigating
  // away mid-upload made the process look truncated when the user came back (the page
  // unmounts, losing the in-progress state). Block navigation until it reaches a terminal
  // state (DONE/ERROR/DUPLICATE/IDLE). CHOOSE_ACTION is excluded — it waits on the user.
  const uploadBusy = status === STATUS.HASHING || status === STATUS.REQUESTING || status === STATUS.UPLOADING;

  // Warn before a browser refresh/tab-close while an upload or move is in flight — the
  // in-app overlay can't stop a native reload (e.g. clicking the "new version" banner),
  // which would abort the in-progress upload.
  useEffect(() => {
    const batchRunning = !!batchQueue && !batchDone;
    if (!uploadBusy && !moveBusy && !batchRunning) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [uploadBusy, moveBusy, batchQueue, batchDone]);

  // Lightweight row ⋯ kebab menu: close it on any outside click. The kebab button and
  // the menu itself stopPropagation, so clicking either won't trigger this close.
  useEffect(() => {
    if (!openMenuKey) return;
    const close = () => setOpenMenuKey(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuKey]);

  // The expanded cleaning report is shared (only one file expands at a time). Extracted
  // so the Active and Archived tabs render the SAME lean report (verdict-first, neutral
  // numbers, phase stats behind Details). Uses fileReport/reportLoading/reportDetailsOpen.
  // fix-source-routing: the customer confirms or changes a data set's platform. Same auth +
  // project headers as every other call; the list refreshes afterwards; errors show the
  // backend's own sentence.
  const setPlatform = async (category, platform) => {
    if (!category || !platform) return;
    setPlatformSaving(category); setPlatformError(null);
    try {
      const token = await getFreshToken() || idToken;
      const res = await fetch(`${API_URL}/upload/categories/${encodeURIComponent(category)}/platform`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", "X-Project-Id": activeProjectId },
        body: JSON.stringify({ platform }),
      });
      if (!res.ok) {
        let detail = "";
        try { const d = await res.json(); detail = typeof d.detail === "string" ? d.detail : ""; } catch { /* no body */ }
        setPlatformError({ category, text: detail || "We couldn't save the platform right now. Please try again." });
        return;
      }
      await fetchFiles(true);
    } catch (err) {
      setPlatformError({ category, text: "We couldn't save the platform right now. Please try again." });
    } finally {
      setPlatformSaving(null);
    }
  };

  // The platform pill (+ change/confirm controls on the first listed file of each data set).
  const renderPlatformControls = (category, withControls) => {
    if (!category) return null;
    const tag = categoryPlatformMap[category] || null;
    const pill = platformPillText(tag);
    const detected = isDetected(tag);
    const busy = platformSaving === category;
    const err = platformError && platformError.category === category ? platformError.text : null;
    if (!pill && !(withControls && platformChoices.length)) return null;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", flexWrap: "wrap", marginRight: "6px" }}>
        {pill && (
          <span title={tag && tag.text ? tag.text : undefined}
            style={{ fontSize: "11px", fontWeight: 600, padding: "1px 8px", borderRadius: "999px",
              background: detected ? "#fff7ed" : "#eef2ff", color: detected ? "#9a3412" : "#3730a3",
              border: `1px solid ${detected ? "#fed7aa" : "#c7d2fe"}` }}>{pill}</span>
        )}
        {withControls && platformChoices.length > 0 && (
          <select aria-label={`Platform for ${category}`} value={tag && tag.key ? tag.key : ""} disabled={busy}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { if (e.target.value) setPlatform(category, e.target.value); }}
            style={{ fontSize: "11px", padding: "1px 4px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff" }}>
            {!(tag && tag.key) && <option value="" disabled>Set platform…</option>}
            {platformChoices.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        )}
        {withControls && tag && tag.key && detected && (
          <button type="button" disabled={busy} onClick={(e) => { e.stopPropagation(); setPlatform(category, tag.key); }}
            style={{ fontSize: "11px", fontWeight: 600, padding: "1px 8px", borderRadius: "6px", border: "1px solid #c7d2fe", background: "#fff", color: "#4338ca", cursor: busy ? "default" : "pointer" }}>
            Confirm
          </button>
        )}
        {err && <span role="alert" style={{ fontSize: "11px", color: "#b91c1c" }}>{err}</span>}
      </span>
    );
  };

  const renderCleaningReport = () => (
    <div className="s3upload-report">
      {fileReport ? (
        <>
          {/* Verdict leads — the part that needs a decision (amber/green banner + message) */}
          {(() => {
            const iv = fileReport.integrity;
            const qc = fileReport.qualityControl;
            let color, label, msg;
            if (iv && iv.verdict) {
              color = iv.verdict === "PASS" ? "#16a34a" : iv.verdict === "WARN" ? "#d97706" : "#dc2626";
              label = iv.verdict === "PASS" ? "✓ Passed" : iv.verdict === "WARN" ? "⚠ Review before continuing" : "✕ Blocked";
              msg = iv.summary || "Totals and row counts reconciled after cleaning.";
            } else if (qc) {
              color = qc.status === "PASS" ? "#16a34a" : qc.status === "PARTIAL" ? "#d97706" : "#dc2626";
              label = qc.status === "PASS" ? "✓ Passed" : `⚠ ${qc.status}`;
              msg = (qc.issues && qc.issues.length) ? qc.issues.join(" · ") : "Cleaned, normalized and de-duplicated.";
            } else {
              color = "#16a34a"; label = "✓ Clean"; msg = "Cleaned, normalized and de-duplicated.";
            }
            return (
              <div className="s3upload-verdict" style={{ background: `${color}12`, borderColor: `${color}44` }}>
                <span className="s3upload-verdict-badge" style={{ background: color }}>{label}</span>
                <div className="s3upload-verdict-msg">{msg}</div>
              </div>
            );
          })()}
          {fileReport.platform && fileReport.platform.text && (
            <div style={{ fontSize: "12px", color: "#475569", margin: "6px 0 2px" }}>{fileReport.platform.text}</div>
          )}

          {/* Progressive disclosure: phase stats stay collapsed until the user opens Details */}
          <button className="s3upload-details-toggle" onClick={(e) => { e.stopPropagation(); setReportDetailsOpen((o) => !o); }}>
            Details <ChevronDown size={13} style={{ transform: reportDetailsOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </button>

          {reportDetailsOpen && (() => {
            const stat = (val, label) => (
              <div key={label} className="s3upload-stat-cell">
                <div className="s3upload-stat-num">{typeof val === "number" ? val.toLocaleString() : val}</div>
                <div className="s3upload-stat-lbl">{label}</div>
              </div>
            );
            const dups = (fileReport.internalDuplicates || 0) + (fileReport.crossFileDuplicates || 0);
            return (
              <div className="s3upload-details">
                {/* QC checklist (kept — same data as before) */}
                {fileReport.qualityControl && (
                  <div className="s3upload-qc-line">
                    {[["Classified", fileReport.qualityControl.classified], ["Cleaned", fileReport.qualityControl.cleaned], ["Normalized", fileReport.qualityControl.normalized], ["Deduplicated", fileReport.qualityControl.deduplicated]].map(([label, ok]) => (
                      <span key={label} className="s3upload-qc-flag" style={{ color: ok ? "#16a34a" : "#94a3b8" }}>{ok ? "✓" : "—"} {label}</span>
                    ))}
                  </div>
                )}
                {/* Phase 1 — Data profiling */}
                <div className="s3upload-phase-label">1 · Data profiling</div>
                <div className="s3upload-stat-grid">
                  {stat(fileReport.originalRows ?? fileReport.importedRows ?? 0, "Rows analyzed")}
                  {stat(fileReport.columnsStandardized ?? 0, "Columns")}
                  {dups > 0 && stat(dups, "Duplicates found")}
                </div>
                {/* Phase 2 — Cleaning & standardization */}
                <div className="s3upload-phase-label">2 · Cleaning &amp; standardization</div>
                <div className="s3upload-stat-grid">
                  {stat(fileReport.importedRows ?? 0, "Rows imported")}
                  {fileReport.droppedRows > 0 && stat(fileReport.droppedRows, "Rows removed")}
                  {fileReport.internalDuplicates > 0 && stat(fileReport.internalDuplicates, "Internal duplicates")}
                  {fileReport.crossFileDuplicates > 0 && stat(fileReport.crossFileDuplicates, "Cross-file duplicates")}
                  {fileReport.phonesNormalized > 0 && stat(fileReport.phonesNormalized, "Phones normalized")}
                  {fileReport.datesNormalized > 0 && stat(fileReport.datesNormalized, "Dates standardized")}
                  {fileReport.emailsNormalized > 0 && stat(fileReport.emailsNormalized, "Emails normalized")}
                  {fileReport.emptyFieldsFilled > 0 && stat(fileReport.emptyFieldsFilled, "Fields filled")}
                </div>
                {/* Reassure on an already-clean file: when nothing was removed or standardized,
                    say so explicitly instead of leaving a near-empty Phase 2. */}
                {!(fileReport.droppedRows > 0 || fileReport.internalDuplicates > 0 || fileReport.crossFileDuplicates > 0 || fileReport.phonesNormalized > 0 || fileReport.datesNormalized > 0 || fileReport.emailsNormalized > 0 || fileReport.emptyFieldsFilled > 0) && (
                  <div className="s3upload-clean-note">Already clean — no duplicates, invalid rows, or values needed standardizing. All rows imported as-is.</div>
                )}
                {/* Phase 3 — Integrity check (numbered here for completeness; also summarized in
                    the verdict banner at the top of the report). */}
                <div className="s3upload-phase-label">3 · Integrity check</div>
                <div className="s3upload-integrity-line">
                  {(() => {
                    const iv = fileReport.integrity;
                    const v = (iv && iv.verdict) ? iv.verdict : "PASS";
                    const c = v === "PASS" ? "#16a34a" : v === "WARN" ? "#d97706" : "#dc2626";
                    const t = v === "PASS" ? "Passed" : v === "WARN" ? "Review" : "Blocked";
                    return (
                      <>
                        <span className="s3upload-qc-flag" style={{ color: c }}>{v === "PASS" ? "✓" : "⚠"} {t}</span>
                        <span className="s3upload-integrity-msg">{(iv && iv.summary) || "Totals and row counts reconcile after cleaning, with no unexpected data loss."}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })()}
        </>
      ) : (
        <p className="s3upload-report-empty">
          {reportLoading
            ? "Loading report..."
            : reportNotReady
              // The file exists but its report wasn't retrieved yet (read-timing miss on a
              // just-finalized upload). Say it's being prepared + offer a manual retry, instead
              // of the scary terminal "no metrics / Reprocess" copy for a report that exists.
              ? (
                <>
                  Report is being generated…{" "}
                  <button
                    type="button"
                    className="s3upload-link-btn"
                    onClick={() => { const k = expandedFile; if (k) { setExpandedFile(null); setTimeout(() => toggleStats(k), 0); } }}
                  >
                    Retry
                  </button>
                </>
              )
              // Only after a retry still returns nothing for an existing file do we show the
              // terminal Reprocess copy — at this point the report genuinely isn't there.
              : "No processing metrics available for this file. Use Reprocess to clean it and generate a report."}
        </p>
      )}
    </div>
  );

  return (
    <div className="s3upload-root">
      {/* In-app feedback (replaces native alert/confirm/prompt) */}
      <ToastView toast={toast} onClose={dismiss} />
      <ConfirmDialog {...confirmDialogProps} />
      <PromptDialog {...promptDialogProps} />
      {uploadBusy && !batchQueue && createPortal(
        // Full-viewport BLOCKING overlay while an upload is in flight — same pattern as the
        // move overlay. Rendered via a PORTAL to document.body so it escapes the page's
        // stacking context and covers the sidebar + menu, swallowing clicks so nothing can
        // be navigated until the upload finishes (leaving mid-upload unmounts this page and
        // makes the upload look aborted).
        <div
          role="alertdialog"
          aria-busy="true"
          aria-label="Uploading file"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed", inset: 0, zIndex: 2147483000, cursor: "wait",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(15,23,42,0.55)", backdropFilter: "blur(1.5px)",
          }}
        >
          <div style={{
            display: "flex", flexDirection: "column", gap: 12,
            background: "#111827", color: "#fff", padding: "18px 22px",
            borderRadius: 12, boxShadow: "0 12px 36px rgba(0,0,0,0.4)",
            fontSize: 14, fontWeight: 500, maxWidth: "90vw", minWidth: 300,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <RefreshCw size={18} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
              <span>{message || "Processing your file…"}</span>
            </div>
            {status === STATUS.UPLOADING && progress > 0 && progress < 100 && (
              <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,0.18)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progress}%`, background: "#818cf8", transition: "width 0.2s" }} />
              </div>
            )}
            <span style={{ fontSize: 12, fontWeight: 400, color: "#cbd5e1" }}>
              Please keep this open until it finishes — don't navigate away.
            </span>
          </div>
        </div>,
        document.body
      )}
      {moveBusy && createPortal(
        // Full-viewport BLOCKING overlay while a move is in flight. A move re-cleans +
        // de-duplicates server-side and isn't atomic from the UI's view; letting the user
        // switch projects/tabs mid-move would repoint the post-move refresh/sync at the
        // wrong project. Rendered via a PORTAL to document.body so it escapes any ancestor
        // stacking context (the page is nested inside the app layout) and truly covers the
        // sidebar + project switcher — swallowing clicks so nothing can be navigated until
        // the move completes.
        <div
          role="alertdialog"
          aria-busy="true"
          aria-label="Moving file"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed", inset: 0, zIndex: 2147483000, cursor: "wait",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(15,23,42,0.55)", backdropFilter: "blur(1.5px)",
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            background: "#111827", color: "#fff", padding: "16px 22px",
            borderRadius: 12, boxShadow: "0 12px 36px rgba(0,0,0,0.4)",
            fontSize: 14, fontWeight: 500, maxWidth: "90vw",
          }}>
            <RefreshCw size={18} style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
            <span>Moving file to “{moveBusy.name}” — re-cleaning &amp; de-duplicating.<br />
              Please keep this open; it only takes a few seconds…</span>
          </div>
        </div>,
        document.body
      )}
      {/* Header — slim: title + subtitle, with count pills + a trust icon on the right.
          Replaces the 3 stat cards + the full-width trust banner (leaner, calmer). */}
      <div className="s3upload-header" style={{ justifyContent: "space-between", marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div className="s3upload-header-icon"><Cloud size={22} color="#fff" /></div>
          <div>
            <h1>File Upload</h1>
            <p>Clean, de-duplicated data that powers your AI Insights.</p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "11.5px", fontWeight: 600, padding: "3px 10px", borderRadius: "999px", background: "#eef2ff", color: "#4f46e5" }}>{activeFiles.length} active</span>
          <span style={{ fontSize: "11.5px", fontWeight: 600, padding: "3px 10px", borderRadius: "999px", background: "#f1f5f9", color: "#475569" }}>{archivedFiles.length} archived</span>
          <span title="End-to-end encryption · Tenant-isolated storage · Duplicate detection · Recoverable archive"
                style={{ color: "#16a34a", display: "flex", alignItems: "center", cursor: "default", paddingLeft: "2px" }}><Lock size={16} /></span>
        </div>
      </div>

      {/* Active project bar — you're uploading INTO this project. Inline switch +
          context (Option 1), matching AI Insights; the sidebar stays in sync. */}
      <ProjectBar />

      {/* One consolidated Files card (v2): the whole card is the drag-and-drop target
          (getRootProps → same onDragOver/Enter/Leave/Drop as before), with Active/Archived
          tabs, search, category filter, "＋ Add file", dense single-line rows, and the lean
          cleaning report. The old dropzone box is gone in the idle state — the card IS the
          drop zone; an indigo highlight shows on dragover. */}
      <div {...getRootProps()} className={`s3upload-card s3upload-files-card ${isDragActive ? "dragging" : ""} ${status === STATUS.UPLOADING ? "uploading" : ""}`}>
        <input {...getInputProps()} />

        {/* Card header: tabs (with counts) · search · category filter · drag hint · Add file */}
        <div className="s3upload-files-head">
          <div className="s3upload-tabs">
            <button type="button" className={`s3upload-tab ${filesTab === "active" ? "on" : ""}`} onClick={() => { setFilesTab("active"); setOpenMenuKey(null); }}>
              Active <span className="s3upload-tab-count">{activeFiles.length}</span>
            </button>
            <button type="button" className={`s3upload-tab ${filesTab === "archived" ? "on" : ""}`} onClick={() => { setFilesTab("archived"); setOpenMenuKey(null); }}>
              Archived <span className="s3upload-tab-count">{archivedFiles.length}</span>
            </button>
            {tableViewOn && (
              <button type="button" className={`s3upload-tab ${filesTab === "tables" ? "on" : ""}`}
                onClick={() => { setFilesTab("tables"); setOpenMenuKey(null); }}>
                Tables
              </button>
            )}
          </div>
          <div className="s3upload-head-actions">
            {filesTab === "active" && (
              <>
                <input
                  type="text"
                  placeholder="Search files…"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="s3upload-search"
                />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="s3upload-cat-select"
                >
                  <option value="all">All categories</option>
                  {[...new Set(activeFiles.map((f) => f.category).filter(Boolean))].sort().map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </>
            )}
            <span className="s3upload-drag-hint" title="Supported file types: .csv, .xlsx (modern Excel), and .xls (legacy Excel)">· drag a file anywhere</span>
            <button type="button" onClick={open} disabled={uploadBusy || !!batchQueue} className="s3upload-add-btn">＋ Add file</button>
            <button type="button" onClick={(e) => { fetchFiles(e); setTablesNonce((n) => n + 1); }} title="Refresh file list" aria-label="Refresh file list" className="s3upload-refresh-icon"><RefreshCw size={13} /></button>
          </div>
        </div>

        {/* Upload feedback — every non-idle state renders here (idle shows no box; the
            card itself is the drop zone). Progress/HASHING are also mirrored by the
            full-screen blocking overlay above. */}
        {/* Multi-file SEQUENTIAL queue — shown instead of the single-file feedback while a batch
            runs (and as a summary when done). One file at a time; a failed/flagged file doesn't
            stop the rest. */}
        {/* Only show the batch under the project it's actually uploading INTO. The queue lives in
            an app-level provider and survives a project switch, so without this guard it would
            render under whatever project is now selected and imply those files are going there —
            they're not (each file is pinned to batchProjectId). A batch with no owner recorded
            (batchProjectId null — shouldn't happen, but a pre-existing in-flight batch from before
            this change would) falls back to showing, so we never HIDE a real upload. */}
        {shouldShowBatch(batchQueue, batchProjectId, activeProjectId) && (
          <div className="s3upload-feedback">
            <div className="s3upload-batch">
              {(() => {
                const total = batchQueue.length;
                const finished = batchQueue.filter((it) => it.status !== "queued" && it.status !== "active").length;
                const okCount = batchQueue.filter((it) => it.status === "done").length;
                const dupCount = batchQueue.filter((it) => it.status === "dup").length;
                const failCount = batchQueue.filter((it) => it.status === "failed").length;
                const activeIdx = batchQueue.findIndex((it) => it.status === "active");
                return (
                  <>
                    <div className="s3upload-batch-head">
                      {!batchDone ? (
                        <span className="s3upload-batch-title">
                          <RefreshCw size={15} className="s3upload-spin" /> Uploading {Math.min(activeIdx + 1 < 1 ? finished : activeIdx + 1, total)} of {total}
                        </span>
                      ) : (
                        <span className="s3upload-batch-title done">✓ {total} file{total !== 1 ? "s" : ""} processed — {okCount} imported{dupCount ? `, ${dupCount} already present` : ""}{failCount ? `, ${failCount} need attention` : ""}</span>
                      )}
                      {!batchDone
                        ? <span className="s3upload-batch-note">Please keep this open — files import one at a time</span>
                        : <button className="s3upload-btn refresh" onClick={() => { setBatchQueue(null); setBatchDone(false); setBatchProjectId(null); }}>Done</button>}
                    </div>
                    {!batchDone && (
                      <div className="s3upload-batch-bar"><i style={{ width: `${total ? Math.round((finished / total) * 100) : 0}%` }} /></div>
                    )}
                    <div className="s3upload-batch-list">
                      {batchQueue.map((it, idx) => {
                        const icon = it.status === "done" ? "✓" : it.status === "dup" ? "≡" : it.status === "failed" ? "✕" : it.status === "active" ? "⟳" : "•";
                        const color = it.status === "done" ? "#16a34a" : it.status === "failed" ? "#dc2626" : it.status === "active" ? "#4f46e5" : "#94a3b8";
                        return (
                          <div key={idx} className={`s3upload-batch-row ${it.status === "active" ? "active" : ""}`}>
                            <span className={`s3upload-batch-ico ${it.status === "active" ? "s3upload-spin" : ""}`} style={{ color }}>{icon}</span>
                            <span className="s3upload-batch-name" title={friendlyFileName(it.name)}>{friendlyFileName(it.name)}</span>
                            <span className="s3upload-batch-detail">{it.detail}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {status !== STATUS.IDLE && !batchQueue && (
          <div className="s3upload-feedback">
          {(status === STATUS.HASHING || status === STATUS.REQUESTING) && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
              <RefreshCw size={28} color="#6366f1" style={{ animation: "spin 1s linear infinite" }} />
              <p style={{ fontSize: "14px", color: "#475569", margin: 0, fontWeight: 500 }}>{message}</p>
            </div>
          )}

          {status === STATUS.UPLOADING && (
            <div className="s3upload-progress-wrap">
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", justifyContent: "center" }}>
                <FileText size={18} color="#6366f1" />
                <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{fileName}</span>
              </div>
              <div className="s3upload-progress-bar"><div className="s3upload-progress-fill" style={{ width: `${progress}%` }} /></div>
              <p style={{ fontSize: "12px", color: "#64748b", margin: 0, textAlign: "center" }}>{progress}% uploaded</p>
            </div>
          )}

          {status === STATUS.DONE && (() => {
            const isWarn = message.startsWith("⚠️");
            // Parse "N label" stat segments from the summary message.
            const stats = message.split(" · ")
              .map(p => p.replace(/^[^0-9]*?(\d)/, '$1').replace("No clean file created.", "").trim())
              .filter(p => p && /^\d/.test(p))
              .map(p => { const [value, ...l] = p.split(" "); return { value, label: l.join(" ") }; });
            const primary = !isWarn ? stats[0] : null;   // rows imported
            const chips = isWarn ? stats : stats.slice(1);
            return (
              <div className="s3upload-done">
                <div className="s3upload-done-head">
                  <CheckCircle2 size={18} color={isWarn ? "#d97706" : "#16a34a"} />
                  <span className="s3upload-done-title" style={{ color: isWarn ? "#b45309" : "#15803d" }}>
                    {isWarn ? "No new data" : "Upload complete"}
                  </span>
                  {fileName && <span className="s3upload-done-file">· {fileName}</span>}
                </div>
                {isWarn && (
                  <p className="s3upload-done-note">All rows already exist in your active data — nothing new was added.</p>
                )}
                {primary && (
                  <div className="s3upload-done-primary"><b>{primary.value}</b>{primary.label}</div>
                )}
                {chips.length > 0 && (
                  <div className="s3upload-done-chips">
                    {chips.map((s, i) => (
                      <span key={i} className="s3upload-chip"><b>{s.value}</b> {s.label}</span>
                    ))}
                  </div>
                )}
                <button onClick={(e) => { e.stopPropagation(); reset(); }} className="s3upload-btn refresh" style={{ marginTop: "4px" }}>Upload another</button>
              </div>
            );
          })()}

          {status === STATUS.DUPLICATE && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <AlertCircle size={36} color={archivedFileKey ? "#d97706" : "#dc2626"} />
              <p style={{ fontSize: "15px", fontWeight: 600, color: archivedFileKey ? "#92400e" : "#991b1b", margin: 0 }}>{archivedFileKey ? "File Found in Archive" : "Duplicate Detected"}</p>
              <p style={{ fontSize: "12px", color: "#64748b", margin: 0, textAlign: "center", maxWidth: "380px" }}>{message}</p>
              <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                {archivedFileKey && (
                  <button onClick={async (e) => { e.stopPropagation(); await restoreFile(archivedFileKey); reset(); }} className="s3upload-btn restore"><RotateCcw size={12} /> Restore File</button>
                )}
                <button onClick={(e) => { e.stopPropagation(); reset(); }} className="s3upload-btn refresh">{archivedFileKey ? "Cancel" : "Try different file"}</button>
              </div>
            </div>
          )}

          {status === STATUS.CHOOSE_ACTION && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
              <FolderOpen size={36} color="#d97706" />
              <p style={{ fontSize: "14px", fontWeight: 600, color: "#92400e", margin: 0 }}>Files Already Exist</p>
              <p style={{ fontSize: "12px", color: "#78350f", margin: 0, textAlign: "center", maxWidth: "400px" }}>{message}</p>
              <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                <button onClick={(e) => { e.stopPropagation(); handleUploadChoice('add'); }} style={{
                  padding: "8px 16px", fontSize: "12px", fontWeight: 600,
                  border: "1px solid #bbf7d0", borderRadius: "6px",
                  background: "#f0fdf4", color: "#166534", cursor: "pointer"
                }}>Add to existing</button>
                <button onClick={(e) => { e.stopPropagation(); handleUploadChoice('replace'); }} style={{
                  padding: "8px 16px", fontSize: "12px", fontWeight: 600,
                  border: "1px solid #fed7aa", borderRadius: "6px",
                  background: "#fff7ed", color: "#c2410c", cursor: "pointer"
                }}>Replace all</button>
                <button onClick={(e) => { e.stopPropagation(); reset(); setPendingUpload(null); }} style={{
                  padding: "8px 16px", fontSize: "12px",
                  border: "1px solid #e2e8f0", borderRadius: "6px",
                  background: "#fff", color: "#64748b", cursor: "pointer"
                }}>Cancel</button>
              </div>
            </div>
          )}

          {status === STATUS.ERROR && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <AlertCircle size={36} color="#dc2626" />
              <p style={{ fontSize: "15px", fontWeight: 600, color: "#991b1b", margin: 0 }}>Upload Failed</p>
              <p style={{ fontSize: "12px", color: "#7f1d1d", margin: 0, textAlign: "center", maxWidth: "380px" }}>{message}</p>
              <button onClick={(e) => { e.stopPropagation(); reset(); }} className="s3upload-btn refresh" style={{ marginTop: "8px" }}>Try again</button>
            </div>
          )}
          </div>
        )}

        {/* ── Active tab: file list ── */}
        {filesTab === "active" && (
          loadingFiles ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="s3upload-file-row" style={{ pointerEvents: "none" }}>
                <div className="skeleton" style={{ width: "34px", height: "34px", borderRadius: "9px", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "7px" }}>
                  <div className="skeleton" style={{ width: `${55 - i * 6}%`, height: "11px" }} />
                  <div className="skeleton" style={{ width: `${35 - i * 4}%`, height: "9px" }} />
                </div>
                <div className="skeleton" style={{ width: "64px", height: "26px", borderRadius: "8px", flexShrink: 0 }} />
              </div>
            ))}
          </div>
        ) : activeFiles.length === 0 ? (
          <div className="s3upload-empty">
            <div className="s3upload-empty-icon"><Upload size={22} color="#cbd5e1" /></div>
            <p>This project has no files yet.</p>
            <p style={{ fontSize: "12.5px", color: "#94a3b8", marginTop: "4px" }}>
              Use <b>＋ Add file</b> (or drag a file onto this card) — or switch to another project in the left sidebar to see its files.
            </p>
          </div>
        ) : (
          (() => {
            const visible = activeFiles.filter(f => {
              if (searchFilter && !friendlyFileName(f.file_name).toLowerCase().includes(searchFilter.toLowerCase())) return false;
              if (categoryFilter !== "all" && f.category !== categoryFilter) return false;
              return true;
            });
            const firstKeyOfCategory = {};
            visible.forEach((f) => { if (f.category && !(f.category in firstKeyOfCategory)) firstKeyOfCategory[f.category] = f.key; });
            return visible.map((file) => (
            <React.Fragment key={file.key}>
              <div className="s3upload-file-row dense">
                <div className="s3upload-file-icon"><FileText size={16} color="#6366f1" /></div>
                <div className="s3upload-file-info">
                  <div className="s3upload-file-name" title={friendlyFileName(file.file_name)}>
                    {friendlyFileName(file.file_name)}
                    <QcChip verdict={file.integrity_verdict} summary={file.integrity_summary} />
                  </div>
                  <div className="s3upload-file-meta">
                    {file.category && <span className="s3upload-cat-pill">{file.category}</span>}
                    {renderPlatformControls(file.category, firstKeyOfCategory[file.category] === file.key)}
                    {formatFileSize(file.size)} · {formatDate(file.last_modified)}
                    {" · "}
                    <span
                      className="s3upload-report-link"
                      onClick={(e) => { e.stopPropagation(); toggleStats(file.key); }}
                      title="View this file's cleaning report — profiling, standardization, and the integrity check"
                    >Cleaning report {expandedFile === file.key ? "▴" : "▾"}</span>
                  </div>
                </div>
                {/* Move flow keeps its inline select; otherwise the ⋯ kebab holds the rest */}
                {movingFile === file.key ? (
                  <select autoFocus defaultValue="" disabled={actionLoading === file.key}
                    onChange={(e) => { if (e.target.value) moveFile(file.key, e.target.value); }}
                    onBlur={() => setMovingFile(null)}
                    className="s3upload-move-select">
                    <option value="" disabled>Move to…</option>
                    {projects.filter((p) => p.projectId !== activeProjectId).map((p) => (
                      <option key={p.projectId} value={p.projectId}>{p.displayName}</option>
                    ))}
                  </select>
                ) : (
                  <div className="s3upload-kebab-wrap" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="s3upload-kebab"
                      title="More actions"
                      aria-label="More actions"
                      disabled={actionLoading === file.key}
                      onClick={(e) => { e.stopPropagation(); setOpenMenuKey(openMenuKey === file.key ? null : file.key); }}
                    ><MoreHorizontal size={16} /></button>
                    {openMenuKey === file.key && (
                      <div className="s3upload-menu" role="menu">
                        <button type="button" role="menuitem" className="s3upload-menu-item" disabled={actionLoading === file.key}
                          title="Download the cleaned, de-duplicated data for this file (CSV) — exactly what was imported"
                          onClick={() => { setOpenMenuKey(null); downloadCleanFile(file.key); }}>
                          <Download size={14} /> Download clean CSV
                        </button>
                        <button type="button" role="menuitem" className="s3upload-menu-item" disabled={actionLoading === file.key}
                          title="Re-clean from the original and optionally assign a category"
                          onClick={() => { setOpenMenuKey(null); reprocessFile(file.key); }}>
                          <RefreshCw size={14} /> Reprocess
                        </button>
                        {projects.filter((p) => p.projectId !== activeProjectId).length > 0 && (
                          <button type="button" role="menuitem" className="s3upload-menu-item" disabled={actionLoading === file.key}
                            title="Move this file to another project (re-cleans both)"
                            onClick={() => { setOpenMenuKey(null); setMovingFile(file.key); }}>
                            <FolderOpen size={14} /> Move to project…
                          </button>
                        )}
                        <div className="s3upload-menu-divider" />
                        <button type="button" role="menuitem" className="s3upload-menu-item danger" disabled={actionLoading === file.key}
                          title="Remove this file from your active dataset (recoverable via Restore, which re-cleans and de-duplicates it)"
                          onClick={() => { setOpenMenuKey(null); archiveFile(file.key, file.category); }}>
                          <Archive size={14} /> Archive
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {expandedFile === file.key && renderCleaningReport()}
            </React.Fragment>
          ));
          })()
          )
        )}

        {/* ── Archived tab: file list ── */}
        {filesTab === "archived" && (
          archivedFiles.length === 0 ? (
            <div className="s3upload-empty">
              <div className="s3upload-empty-icon"><Archive size={22} color="#cbd5e1" /></div>
              <p>No archived files in this project.</p>
              <p style={{ fontSize: "12.5px", color: "#94a3b8", marginTop: "4px" }}>
                Archived files show up here — restore any of them to bring their unique rows back.
              </p>
            </div>
          ) : (
            <>
              <p className="s3upload-archived-hint">Archived files are hidden from AI Insights. Restore to reactivate.</p>
              {archivedFiles.map((file) => (
                <React.Fragment key={file.key}>
                  <div className="s3upload-file-row dense" style={{ opacity: 0.75 }}>
                    <div className="s3upload-file-icon archived"><FileText size={16} color="#94a3b8" /></div>
                    <div className="s3upload-file-info">
                      <div className="s3upload-file-name" style={{ color: "#64748b" }} title={friendlyFileName(file.file_name)}>
                        {friendlyFileName(file.file_name)}
                        <QcChip verdict={file.integrity_verdict} summary={file.integrity_summary} />
                      </div>
                      <div className="s3upload-file-meta">
                        {formatFileSize(file.size)} · {formatDate(file.last_modified)}
                        {" · "}
                        <span
                          className="s3upload-report-link"
                          onClick={(e) => { e.stopPropagation(); toggleStats(file.key); }}
                          title="View this file's cleaning report"
                        >Cleaning report {expandedFile === file.key ? "▴" : "▾"}</span>
                      </div>
                    </div>
                    <button onClick={() => restoreFile(file.key)} disabled={actionLoading === file.key} title="Bring this file back into your active dataset — it's re-cleaned and de-duplicated against current data" className="s3upload-btn restore"><RotateCcw size={12} /> Restore</button>
                    <div className="s3upload-kebab-wrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="s3upload-kebab"
                        title="More actions"
                        aria-label="More actions"
                        disabled={actionLoading === file.key}
                        onClick={(e) => { e.stopPropagation(); setOpenMenuKey(openMenuKey === file.key ? null : file.key); }}
                      ><MoreHorizontal size={16} /></button>
                      {openMenuKey === file.key && (
                        <div className="s3upload-menu" role="menu">
                          <button type="button" role="menuitem" className="s3upload-menu-item" disabled={actionLoading === file.key}
                            title="Download this archived file's cleaned data (CSV)"
                            onClick={() => { setOpenMenuKey(null); downloadCleanFile(file.key); }}>
                            <Download size={14} /> Download clean CSV
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {expandedFile === file.key && renderCleaningReport()}
                </React.Fragment>
              ))}
            </>
          )
        )}

        {/* ── Tables tab (flag upload_table_structure_view): read-only table → fields view.
            Refetches whenever the file list changes (upload done, archive/restore/move,
            auto-refresh) and when the refresh icon is clicked. ── */}
        {tableViewOn && filesTab === "tables" && (
          <TableStructureView
            idToken={idToken}
            projectId={activeProjectId}
            refreshKey={`${filesSignature(activeFiles, archivedFiles)}|${tablesNonce}`}
          />
        )}
      </div>
    </div>
  );
}
